/* ===== Settings Service ===== */
import { db } from '../config/firebase.js?v=83.21';
import { $, logFirestoreAccessDeniedOnce } from '../utils/helpers.js?v=83.21';
import { sha256 } from '../utils/helpers.js?v=83.21';
import { clearPreview, appendPreview, renderPreview, showSnackbar } from '../utils/ui.js?v=83.21';
import { uploadFile } from './storage.js?v=83.21';
import { renderPrizeOptions } from '../ui/prizes.js?v=83.21';
import { updateClientButtonsGuard, updateTriesCounter, refreshAdminSummaries } from '../ui/admin.js?v=83.21';
import { refreshRoleCards } from '../ui/roles.js?v=83.21';
import { logger } from '../utils/logger.js?v=83.21';
import { sanitizeImageSrc } from '../utils/url.js?v=83.21';
import { 
  validateNonNegativeInteger, 
  validateIntegerRange, 
  validateHexColor, 
  validateDateRange,
  validateDate,
  validateFileUploads,
  validateFileUpload,
  validateCompressibleImageUpload,
  validateReservationPhoneInput,
  validateGoogleMapsUrl,
  validateMenuUrl
} from '../utils/validation.js?v=83.21';
import { saveCampaignSettingsBackend } from './backend.js?v=83.21';
import { getPendingPlaysCount } from './pending-plays.js?v=83.21';
import { getCurrentUser } from './auth.js?v=83.21';
import { sanitizeClientLayoutConfig } from '../ui/client-layout-config.js?v=83.21';
import { hydrateEventModuleAdminFromSettings } from '../ui/event-module-admin.js?v=83.21';
import { hydrateMenuModuleAdminFromSettings } from '../ui/menu-module-admin.js?v=83.21';
import { hydrateContactModuleAdminFromSettings } from '../ui/contact-module-admin.js?v=83.21';

export { saveEventModuleSettings } from '../ui/event-module-admin.js?v=83.21';
export { saveMenuModuleSettings } from '../ui/menu-module-admin.js?v=83.21';
export { saveContactModuleSettings, saveReservationSettings } from '../ui/contact-module-admin.js?v=83.21';
import { withTimeoutMs } from '../utils/with-timeout.js?v=83.21';

const PUBLIC_SITE_SERVER_REFRESH_MS = 10000;

// Get firebase reference for FieldValue
let firebaseRef = null;
if (typeof firebase !== 'undefined') {
  firebaseRef = firebase;
}

// Global state
let currentSettings = null;
let campaignColorPreviewBound = false;

function bindCampaignColorPreview() {
  if (campaignColorPreviewBound) return;
  const el = $('buttonColor');
  if (!el) return;
  campaignColorPreviewBound = true;
  el.addEventListener('change', () => {
    const color = String(el.value || '#43c047').trim();
    if (!currentSettings) currentSettings = {};
    currentSettings.buttonColor = color;
    setCurrentSettings(currentSettings);
    import('../ui/slideshow.js?v=83.21').then((m) => m.applyClientUI()).catch(() => {});
  });
}
let lastSettingsLoadAt = 0;
const SETTINGS_TTL_MS = 4000;
let adminUiPopulatedAt = 0; // când a fost populat/legat ultima dată formularul de admin
const SETTINGS_CACHE_KEY = 'spressjoy_settings';
const SETTINGS_CACHE_TIMESTAMP_KEY = 'spressjoy_settings_timestamp';
const SETTINGS_LOCALSTORAGE_TTL_MS = 7 * 24 * 3600000; // 7 zile - cache validitate pentru localStorage (în loc de 1 oră)
let localBgImages = [];
let hasUnsavedBgChanges = false;
let localFeedbackQrImage = null;
let hasUnsavedFeedbackQrChanges = false;
let localSocialQrImage = null;
let hasUnsavedSocialQrChanges = false;
let localClientPromoGalleryImages = [];
let hasUnsavedClientPromoChanges = false;
let clientPromoReplaceIndex = null;
let localLocationHoursImage = null;
let hasUnsavedLocationHoursChanges = false;
let localLocationLogoImage = null;
let hasUnsavedLocationLogoChanges = false;

function updateUnsavedIndicators() {
  const badge = document.getElementById('bgDirtyBadge');
  if (badge) badge.style.display = hasUnsavedBgChanges ? '' : 'none';
  const feedbackBadge = document.getElementById('feedbackQrDirtyBadge');
  if (feedbackBadge) feedbackBadge.style.display = hasUnsavedFeedbackQrChanges ? '' : 'none';
  const socialBadge = document.getElementById('socialQrDirtyBadge');
  if (socialBadge) socialBadge.style.display = hasUnsavedSocialQrChanges ? '' : 'none';
  const promoBadge = document.getElementById('clientPromoGalleryDirtyBadge');
  if (promoBadge) promoBadge.style.display = hasUnsavedClientPromoChanges ? '' : 'none';
  const locationHoursBadge = document.getElementById('locationHoursDirtyBadge');
  if (locationHoursBadge) locationHoursBadge.style.display = hasUnsavedLocationHoursChanges ? '' : 'none';
  const locationLogoBadge = document.getElementById('locationLogoDirtyBadge');
  if (locationLogoBadge) locationLogoBadge.style.display = hasUnsavedLocationLogoChanges ? '' : 'none';
  const hasAnyUnsaved =
    hasUnsavedBgChanges ||
    hasUnsavedFeedbackQrChanges ||
    hasUnsavedSocialQrChanges ||
    hasUnsavedClientPromoChanges ||
    hasUnsavedLocationHoursChanges ||
    hasUnsavedLocationLogoChanges;
  const saveBtn = document.getElementById('clientInterfaceModuleSaveBtn');
  if (saveBtn) saveBtn.classList.toggle('bg-unsaved', hasAnyUnsaved);
}

function setBgDirtyState(isDirty) {
  hasUnsavedBgChanges = !!isDirty;
  updateUnsavedIndicators();
}

function setFeedbackQrDirtyState(isDirty) {
  hasUnsavedFeedbackQrChanges = !!isDirty;
  updateUnsavedIndicators();
}

function setSocialQrDirtyState(isDirty) {
  hasUnsavedSocialQrChanges = !!isDirty;
  updateUnsavedIndicators();
}

function setClientPromoDirtyState(isDirty) {
  hasUnsavedClientPromoChanges = !!isDirty;
  updateUnsavedIndicators();
}

function setLocationHoursDirtyState(isDirty) {
  hasUnsavedLocationHoursChanges = !!isDirty;
  updateUnsavedIndicators();
}

function setLocationLogoDirtyState(isDirty) {
  hasUnsavedLocationLogoChanges = !!isDirty;
  updateUnsavedIndicators();
}

function syncLocationHoursEnabledLabel() {
  const label = document.getElementById('locationHoursEnabledLabel');
  const chk = document.getElementById('locationHoursEnabled');
  if (!label || !chk) return;
  label.textContent = chk.checked ? 'Program locație activ' : 'Program locație inactiv';
}

function syncLocationMapEnabledLabel() {
  const label = document.getElementById('locationMapEnabledLabel');
  const chk = document.getElementById('locationMapEnabled');
  if (!label || !chk) return;
  label.textContent = chk.checked ? 'Locație / Hartă activă' : 'Locație / Hartă inactivă';
}

function readLocationMapFieldsFromForm() {
  const enabled = $('locationMapEnabled')?.checked === true;
  const name = String($('locationMapName')?.value || '').trim().slice(0, 80);
  const address = String($('locationMapAddress')?.value || '').trim().slice(0, 200);
  const urlRaw = $('locationMapUrl')?.value || '';
  const urlValidation = urlRaw.trim() ? validateGoogleMapsUrl(urlRaw) : { valid: true, value: '' };
  return { enabled, name, address, urlValidation };
}

function getSettingsBgUrls() {
  return currentSettings?.bgURLs || (currentSettings?.bgURL ? [currentSettings.bgURL] : []);
}

function resetLocalBgImagesFromSettings() {
  if (Array.isArray(localBgImages)) {
    localBgImages.forEach((item) => {
      if (item?.type === 'local' && item.previewUrl) {
        try {
          URL.revokeObjectURL(item.previewUrl);
        } catch (e) {
          // Non-critical
        }
      }
    });
  }
  localBgImages = getSettingsBgUrls().map((url) => ({
    type: 'remote',
    url
  }));
  setBgDirtyState(false);
}

function resetLocalFeedbackQrImageFromSettings() {
  if (localFeedbackQrImage?.type === 'local' && localFeedbackQrImage.previewUrl) {
    try {
      URL.revokeObjectURL(localFeedbackQrImage.previewUrl);
    } catch (e) {
      // Non-critical
    }
  }
  const existingUrl = String(currentSettings?.feedbackQrURL || '').trim();
  if (existingUrl) {
    localFeedbackQrImage = { type: 'remote', url: existingUrl };
  } else {
    localFeedbackQrImage = null;
  }
  setFeedbackQrDirtyState(false);
}

function resetLocalLocationLogoImageFromSettings() {
  if (localLocationLogoImage?.type === 'local' && localLocationLogoImage.previewUrl) {
    try {
      URL.revokeObjectURL(localLocationLogoImage.previewUrl);
    } catch (e) {
      // Non-critical
    }
  }
  const existingUrl = String(currentSettings?.locationLogoURL || '').trim();
  if (existingUrl) {
    localLocationLogoImage = { type: 'remote', url: existingUrl };
  } else {
    localLocationLogoImage = null;
  }
  setLocationLogoDirtyState(false);
}

function resetLocalLocationHoursImageFromSettings() {
  if (localLocationHoursImage?.type === 'local' && localLocationHoursImage.previewUrl) {
    try {
      URL.revokeObjectURL(localLocationHoursImage.previewUrl);
    } catch (e) {
      // Non-critical
    }
  }
  const existingUrl = String(currentSettings?.locationHoursImageURL || '').trim();
  if (existingUrl) {
    localLocationHoursImage = { type: 'remote', url: existingUrl };
  } else {
    localLocationHoursImage = null;
  }
  setLocationHoursDirtyState(false);
}

function resetLocalSocialQrImageFromSettings() {
  if (localSocialQrImage?.type === 'local' && localSocialQrImage.previewUrl) {
    try {
      URL.revokeObjectURL(localSocialQrImage.previewUrl);
    } catch (e) {
      // Non-critical
    }
  }
  const existingUrl = String(currentSettings?.qrURL || '').trim();
  if (existingUrl) {
    localSocialQrImage = { type: 'remote', url: existingUrl };
  } else {
    localSocialQrImage = null;
  }
  setSocialQrDirtyState(false);
}

function resetLocalClientPromoGalleryFromSettings() {
  if (Array.isArray(localClientPromoGalleryImages)) {
    localClientPromoGalleryImages.forEach((item) => {
      if (item?.type === 'local' && item.previewUrl) {
        try {
          URL.revokeObjectURL(item.previewUrl);
        } catch (e) {
          // Non-critical
        }
      }
    });
  }
  const g = currentSettings?.clientPromoGallery;
  const urls = Array.isArray(g?.images) ? g.images : [];
  const labels = Array.isArray(g?.labels) ? g.labels : [];
  const links = Array.isArray(g?.links) ? g.links : [];
  localClientPromoGalleryImages = urls
    .map((u, i) => ({ url: String(u || '').trim(), label: String(labels[i] || '').trim().slice(0, 40), link: String(links[i] || '').trim().slice(0, 500) }))
    .filter((x) => x.url)
    .slice(0, 10)
    .map(({ url, label, link }) => ({ type: 'remote', url, label, link }));
  setClientPromoDirtyState(false);
}

/** Reîncarcă formularul galeriei din setările curente (ex. după ce Joy a pus poze) — dacă nu ai modificări nesalvate. */
export function hydrateClientPromoGalleryAdminFromSettings() {
  if (hasUnsavedClientPromoChanges) return;
  const promoG = currentSettings?.clientPromoGallery;
  if ($('clientPromoGalleryEnabled')) $('clientPromoGalleryEnabled').checked = promoG?.enabled === true;
  if ($('clientPromoGalleryTitle')) $('clientPromoGalleryTitle').value = String(promoG?.title || '').trim().slice(0, 80);
  resetLocalClientPromoGalleryFromSettings();
  renderClientPromoGalleryAdminList();
  syncClientPromoFieldsVisibility();
}

export function syncClientPromoFieldsVisibility() {
  const en = $('clientPromoGalleryEnabled');
  const fields = $('clientPromoGalleryFields');
  if (!en || !fields) return;
  fields.style.display = en.checked ? 'block' : 'none';
  updateClientPromoValidationHint();
}

function updateClientPromoValidationHint() {
  const hint = $('clientPromoGalleryValidationHint');
  if (!hint) return;
  const en = $('clientPromoGalleryEnabled')?.checked === true;
  const n = Array.isArray(localClientPromoGalleryImages)
    ? localClientPromoGalleryImages.filter((i) => i && (i.url || i.file)).length
    : 0;
  if (en && n < 1) {
    hint.style.display = 'block';
    hint.textContent = 'Pentru afișare pe client, adaugă cel puțin o imagine sau dezactivează blocul.';
  } else {
    hint.style.display = 'none';
    hint.textContent = '';
  }
}

/** Linkul pozei, curățat: gol sau https://… (fără https:// în față, îl adăugăm noi). */
function promoLinkOf(item) {
  let u = String(item?.link || '').trim().slice(0, 500);
  if (!u) return '';
  if (!/^https?:\/\//i.test(u)) u = `https://${u}`;
  if (!/^https:\/\//i.test(u)) throw new Error('Linkul pozei trebuie să înceapă cu https://');
  try { new URL(u); } catch (_) { throw new Error(`Linkul „${u.slice(0, 40)}” nu e valid.`); }
  return u;
}

function renderClientPromoGalleryAdminList() {
  const list = $('clientPromoGalleryList');
  if (!list) return;
  list.replaceChildren();
  if (!Array.isArray(localClientPromoGalleryImages)) {
    localClientPromoGalleryImages = [];
  }
  if (localClientPromoGalleryImages.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'smallnote';
    empty.textContent = 'Nicio imagine încă. Apasă „Adaugă imagine”.';
    list.appendChild(empty);
    updateClientPromoValidationHint();
    return;
  }

  localClientPromoGalleryImages.forEach((item, idx) => {
    const row = document.createElement('div');
    row.className = 'client-promo-gallery-admin-item';
    const safeUrl = sanitizeImageSrc(item?.url || '');
    if (safeUrl) {
      const img = document.createElement('img');
      img.className = 'client-promo-gallery-admin-item__thumb';
      img.src = safeUrl;
      img.alt = '';
      row.appendChild(img);
    }

    // numele pozei (opțional) — apare pe cartonaș în loc de „Ofertă N” și sub poză când e deschisă
    const name = document.createElement('input');
    name.type = 'text';
    name.className = 'client-promo-gallery-admin-item__name';
    name.maxLength = 40;
    name.placeholder = 'Nume pe cartonaș (opțional, ex. Desert)';
    name.setAttribute('aria-label', `Numele pozei ${idx + 1}`);
    name.value = String(item?.label || '');
    name.addEventListener('input', () => { item.label = name.value.slice(0, 40); setClientPromoDirtyState(true); });
    row.appendChild(name);
    // link opțional: la apăsare pe poză se deschide linkul (clip YouTube, comandă online, programare…)
    const link = document.createElement('input');
    link.type = 'url';
    link.inputMode = 'url';
    link.className = 'client-promo-gallery-admin-item__link';
    link.maxLength = 500;
    link.placeholder = 'Link (opțional) · ex. youtube.com/…';
    link.setAttribute('aria-label', `Linkul pozei ${idx + 1}`);
    link.value = String(item?.link || '');
    link.addEventListener('input', () => { item.link = link.value.trim().slice(0, 500); setClientPromoDirtyState(true); });
    row.appendChild(link);

    const actions = document.createElement('div');
    actions.className = 'client-promo-gallery-admin-item__actions';

    const up = document.createElement('button');
    up.type = 'button';
    up.className = 'btn-ghost client-promo-gallery-admin-item__btn';
    up.textContent = '↑';
    up.title = 'Mută mai sus';
    up.setAttribute('aria-label', 'Mută imaginea mai sus');
    up.disabled = idx === 0;
    up.addEventListener('click', () => {
      if (idx <= 0) return;
      const tmp = localClientPromoGalleryImages[idx - 1];
      localClientPromoGalleryImages[idx - 1] = localClientPromoGalleryImages[idx];
      localClientPromoGalleryImages[idx] = tmp;
      setClientPromoDirtyState(true);
      renderClientPromoGalleryAdminList();
    });

    const down = document.createElement('button');
    down.type = 'button';
    down.className = 'btn-ghost client-promo-gallery-admin-item__btn';
    down.textContent = '↓';
    down.title = 'Mută mai jos';
    down.setAttribute('aria-label', 'Mută imaginea mai jos');
    down.disabled = idx >= localClientPromoGalleryImages.length - 1;
    down.addEventListener('click', () => {
      if (idx >= localClientPromoGalleryImages.length - 1) return;
      const tmp = localClientPromoGalleryImages[idx + 1];
      localClientPromoGalleryImages[idx + 1] = localClientPromoGalleryImages[idx];
      localClientPromoGalleryImages[idx] = tmp;
      setClientPromoDirtyState(true);
      renderClientPromoGalleryAdminList();
    });

    const replaceBtn = document.createElement('button');
    replaceBtn.type = 'button';
    replaceBtn.className = 'btn-ghost client-promo-gallery-admin-item__btn';
    replaceBtn.textContent = '⟳';
    replaceBtn.title = 'Înlocuiește imaginea';
    replaceBtn.setAttribute('aria-label', 'Înlocuiește imaginea');
    replaceBtn.addEventListener('click', () => {
      clientPromoReplaceIndex = idx;
      $('clientPromoGalleryUpload')?.click();
    });

    // Ștergere în doi pași (fără dialog): prima apăsare cere confirmarea, a doua șterge.
    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'btn-danger client-promo-gallery-admin-item__btn client-promo-gallery-admin-item__btn--del';
    removeBtn.textContent = '🗑';
    removeBtn.title = 'Șterge imaginea';
    removeBtn.setAttribute('aria-label', 'Șterge imaginea');
    let removeConfirmTimer = null;
    removeBtn.addEventListener('click', () => {
      if (!removeBtn.classList.contains('is-confirming')) {
        removeBtn.classList.add('is-confirming');
        removeBtn.textContent = 'Sigur?';
        removeConfirmTimer = setTimeout(() => {
          removeBtn.classList.remove('is-confirming');
          removeBtn.textContent = '🗑';
        }, 3000);
        return;
      }
      clearTimeout(removeConfirmTimer);
      const removed = localClientPromoGalleryImages[idx];
      if (removed?.type === 'local' && removed.previewUrl) {
        try {
          URL.revokeObjectURL(removed.previewUrl);
        } catch (e) {
          // Non-critical
        }
      }
      localClientPromoGalleryImages.splice(idx, 1);
      setClientPromoDirtyState(true);
      renderClientPromoGalleryAdminList();
    });

    actions.appendChild(up);
    actions.appendChild(down);
    actions.appendChild(replaceBtn);
    actions.appendChild(removeBtn);
    row.appendChild(actions);
    list.appendChild(row);
  });
  updateClientPromoValidationHint();
}

function bindClientPromoGalleryControls() {
  const enabledEl = $('clientPromoGalleryEnabled');
  if (enabledEl && !enabledEl.dataset.clientPromoToggleBound) {
    enabledEl.dataset.clientPromoToggleBound = '1';
    enabledEl.addEventListener('change', () => {
      syncClientPromoFieldsVisibility();
      refreshAdminSummaries();
    });
  }

  const titleEl = $('clientPromoGalleryTitle');
  if (titleEl && !titleEl.dataset.clientPromoTitleBound) {
    titleEl.dataset.clientPromoTitleBound = '1';
    titleEl.addEventListener('input', () => {
      setClientPromoDirtyState(true);
    });
  }

  const uploadEl = $('clientPromoGalleryUpload');
  if (!uploadEl || uploadEl.dataset.boundClientPromo === '1') {
    return;
  }

  const addBtn = $('clientPromoGalleryAddBtn');
  if (addBtn && !addBtn.dataset.clientPromoAddBound) {
    addBtn.dataset.clientPromoAddBound = '1';
    addBtn.addEventListener('click', () => {
      clientPromoReplaceIndex = null;
      uploadEl.click();
    });
  }

  uploadEl.addEventListener('change', () => handlePromoGallerySelection(uploadEl));
  uploadEl.dataset.boundClientPromo = '1';
}

function handlePromoGallerySelection(uploadEl) {
    const file = uploadEl.files?.[0];
    uploadEl.value = '';
    if (!file) {
      clientPromoReplaceIndex = null;
      showSnackbar('Nu am primit nicio poză din galerie. Încearcă din nou sau alege poza din „Fișiere”.', 'error');
      return;
    }
    const fileValidation = validateFileUpload(file, {
      maxSize: 10 * 1024 * 1024,
      allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      fieldName: 'Imagine galerie promo'
    });
    if (!fileValidation.valid) {
      clientPromoReplaceIndex = null;
      showSnackbar(fileValidation.message, 'error');
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    const entry = {
      type: 'local',
      file,
      previewUrl,
      url: previewUrl,
      label: '',
      link: ''
    };

    if (
      clientPromoReplaceIndex !== null &&
      clientPromoReplaceIndex >= 0 &&
      clientPromoReplaceIndex < localClientPromoGalleryImages.length
    ) {
      const old = localClientPromoGalleryImages[clientPromoReplaceIndex];
      entry.label = String(old?.label || ''); // la înlocuirea pozei, numele și linkul rămân
      entry.link = String(old?.link || '');
      if (old?.type === 'local' && old.previewUrl) {
        try {
          URL.revokeObjectURL(old.previewUrl);
        } catch (e) {
          // Non-critical
        }
      }
      localClientPromoGalleryImages[clientPromoReplaceIndex] = entry;
    } else {
      if (localClientPromoGalleryImages.length >= 10) {
        try {
          URL.revokeObjectURL(previewUrl);
        } catch (e) {
          // Non-critical
        }
        showSnackbar('Poți avea maxim 10 imagini în galeria promo.', 'error');
        clientPromoReplaceIndex = null;
        return;
      }
      localClientPromoGalleryImages.push(entry);
    }
    clientPromoReplaceIndex = null;
    setClientPromoDirtyState(true);
    renderClientPromoGalleryAdminList();
}

/* Plasă de siguranță: dacă handlerele de mai sus n-au apucat să se lege (ex. o eroare mai devreme în
   popularea formularului, pe un cont anume), o selecție de poze tot ajunge unde trebuie. */
if (typeof document !== 'undefined' && !window.__sjUploadFallbackBound) {
  window.__sjUploadFallbackBound = true;
  document.addEventListener('change', (e) => {
    const el = e.target;
    if (!el || el.tagName !== 'INPUT' || el.type !== 'file') return;
    try {
      // procesăm selecția acum; handlerul permanent se leagă după ce se termină acest eveniment
      // (un listener adăugat pe țintă în timpul dispatch-ului s-ar executa și el → poze duplicate)
      if (el.id === 'bgUpload' && el.dataset.boundUnsavedPreview !== '1') {
        logger.warn('[Settings] bgUpload nu era legat — folosesc plasa de siguranță');
        handleBgUploadSelection(el);
        setTimeout(bindBgUploadInput, 0);
      } else if (el.id === 'confirmBgUpload' && el.dataset.boundLocalPreview !== '1') {
        handleConfirmBgSelection(el);
        setTimeout(bindConfirmBgUploadInput, 0);
      } else if (el.id === 'clientPromoGalleryUpload' && el.dataset.boundClientPromo !== '1') {
        logger.warn('[Settings] clientPromoGalleryUpload nu era legat — folosesc plasa de siguranță');
        handlePromoGallerySelection(el);
        setTimeout(bindClientPromoGalleryControls, 0);
      }
    } catch (err) {
      logger.error('[Settings] upload fallback', err);
    }
  }, true);
}

let bgReplaceIndex = null;

/** Pozele localului: rânduri ca la galeria promo — mută sus/jos, înlocuiește, șterge (cu confirmare). */
function renderLocalBgPreview() {
  const wrap = document.getElementById('bgPreview');
  if (!wrap) return;
  wrap.replaceChildren();
  wrap.classList.remove('preview-grid');
  wrap.classList.add('client-promo-gallery-admin-list');
  if (!Array.isArray(localBgImages) || localBgImages.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'smallnote';
    empty.textContent = 'Nicio poză încă. Apasă „Adaugă poze”.';
    wrap.appendChild(empty);
    return;
  }

  const mkBtn = (text, label, cls = 'btn-ghost') => {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = `${cls} client-promo-gallery-admin-item__btn`;
    b.textContent = text;
    b.title = label;
    b.setAttribute('aria-label', label);
    return b;
  };
  const move = (from, to) => {
    if (to < 0 || to >= localBgImages.length) return;
    const tmp = localBgImages[to];
    localBgImages[to] = localBgImages[from];
    localBgImages[from] = tmp;
    setBgDirtyState(true);
    renderLocalBgPreview();
  };

  localBgImages.forEach((item, idx) => {
    const safeUrl = sanitizeImageSrc(item?.url || '');
    if (!safeUrl) return;
    const row = document.createElement('div');
    row.className = 'client-promo-gallery-admin-item';
    const img = document.createElement('img');
    img.className = 'client-promo-gallery-admin-item__thumb';
    img.src = safeUrl;
    img.alt = `Poza ${idx + 1}`;
    row.appendChild(img);

    const actions = document.createElement('div');
    actions.className = 'client-promo-gallery-admin-item__actions';
    const up = mkBtn('↑', 'Mută mai sus');
    up.disabled = idx === 0;
    up.addEventListener('click', () => move(idx, idx - 1));
    const down = mkBtn('↓', 'Mută mai jos');
    down.disabled = idx >= localBgImages.length - 1;
    down.addEventListener('click', () => move(idx, idx + 1));
    const replaceBtn = mkBtn('⟳', 'Înlocuiește poza');
    replaceBtn.addEventListener('click', () => {
      bgReplaceIndex = idx;
      $('bgUpload')?.click();
    });
    const removeBtn = mkBtn('🗑', 'Șterge poza', 'btn-danger client-promo-gallery-admin-item__btn--del');
    let t = null;
    removeBtn.addEventListener('click', () => {
      if (!removeBtn.classList.contains('is-confirming')) {
        removeBtn.classList.add('is-confirming');
        removeBtn.textContent = 'Sigur?';
        t = setTimeout(() => { removeBtn.classList.remove('is-confirming'); removeBtn.textContent = '🗑'; }, 3000);
        return;
      }
      clearTimeout(t);
      const removed = localBgImages[idx];
      if (removed?.type === 'local' && removed.previewUrl) {
        try { URL.revokeObjectURL(removed.previewUrl); } catch (e) { /* non-critical */ }
      }
      localBgImages.splice(idx, 1);
      setBgDirtyState(true);
      renderLocalBgPreview();
    });
    actions.append(up, down, replaceBtn, removeBtn);
    row.appendChild(actions);
    wrap.appendChild(row);
  });
}

function renderLocalLocationLogoPreview() {
  const wrap = document.getElementById('locationLogoPreview');
  if (!wrap) return;
  wrap.replaceChildren();

  if (!localLocationLogoImage || !localLocationLogoImage.url) {
    const empty = document.createElement('div');
    empty.className = 'preview-img preview-empty';
    empty.textContent = 'Nesetat';
    wrap.appendChild(empty);
    return;
  }

  const safeUrl = sanitizeImageSrc(localLocationLogoImage.url);
  if (!safeUrl) return;
  const node = document.createElement('div');
  node.className = 'preview-img';
  const img = document.createElement('img');
  img.src = safeUrl;
  img.alt = 'preview-location-logo';
  img.style.maxWidth = '200px';
  img.style.objectFit = 'contain';

  const removeBtn = document.createElement('button');
  removeBtn.type = 'button';
  removeBtn.className = 'preview-remove-btn';
  removeBtn.setAttribute('aria-label', 'Elimină logo din preview');
  removeBtn.textContent = '×';
  removeBtn.addEventListener('click', () => {
    if (localLocationLogoImage?.type === 'local' && localLocationLogoImage.previewUrl) {
      try {
        URL.revokeObjectURL(localLocationLogoImage.previewUrl);
      } catch (e) {
        // Non-critical
      }
    }
    localLocationLogoImage = null;
    setLocationLogoDirtyState(true);
    renderLocalLocationLogoPreview();
  });

  node.appendChild(img);
  node.appendChild(removeBtn);
  wrap.appendChild(node);
}

function renderLocalLocationHoursPreview() {
  const wrap = document.getElementById('locationHoursPreview');
  if (!wrap) return;
  wrap.replaceChildren();

  if (!localLocationHoursImage || !localLocationHoursImage.url) {
    const empty = document.createElement('div');
    empty.className = 'preview-img preview-empty';
    empty.textContent = 'Nesetat';
    wrap.appendChild(empty);
    return;
  }

  const safeUrl = sanitizeImageSrc(localLocationHoursImage.url);
  if (!safeUrl) return;
  const node = document.createElement('div');
  node.className = 'preview-img';
  const img = document.createElement('img');
  img.src = safeUrl;
  img.alt = 'preview-location-hours';

  const removeBtn = document.createElement('button');
  removeBtn.type = 'button';
  removeBtn.className = 'preview-remove-btn';
  removeBtn.setAttribute('aria-label', 'Șterge imaginea program');
  removeBtn.textContent = '×';
  removeBtn.addEventListener('click', () => {
    if (localLocationHoursImage?.type === 'local' && localLocationHoursImage.previewUrl) {
      try {
        URL.revokeObjectURL(localLocationHoursImage.previewUrl);
      } catch (e) {
        // Non-critical
      }
    }
    localLocationHoursImage = null;
    setLocationHoursDirtyState(true);
    renderLocalLocationHoursPreview();
  });

  node.appendChild(img);
  node.appendChild(removeBtn);
  wrap.appendChild(node);
}

function renderLocalFeedbackQrPreview() {
  const wrap = document.getElementById('feedbackQrPreview');
  if (!wrap) return;
  wrap.replaceChildren();

  if (!localFeedbackQrImage || !localFeedbackQrImage.url) {
    const empty = document.createElement('div');
    empty.className = 'preview-img preview-empty';
    empty.textContent = 'Nesetat';
    wrap.appendChild(empty);
    return;
  }

  const safeUrl = sanitizeImageSrc(localFeedbackQrImage.url);
  if (!safeUrl) return;
  const node = document.createElement('div');
  node.className = 'preview-img';
  const img = document.createElement('img');
  img.src = safeUrl;
  img.alt = 'preview-feedback-qr';

  const removeBtn = document.createElement('button');
  removeBtn.type = 'button';
  removeBtn.className = 'preview-remove-btn';
  removeBtn.setAttribute('aria-label', 'Șterge codul QR feedback din listă');
  removeBtn.textContent = '×';
  removeBtn.addEventListener('click', () => {
    if (localFeedbackQrImage?.type === 'local' && localFeedbackQrImage.previewUrl) {
      try {
        URL.revokeObjectURL(localFeedbackQrImage.previewUrl);
      } catch (e) {
        // Non-critical
      }
    }
    localFeedbackQrImage = null;
    setFeedbackQrDirtyState(true);
    renderLocalFeedbackQrPreview();
  });

  node.appendChild(img);
  node.appendChild(removeBtn);
  wrap.appendChild(node);
}

function renderLocalSocialQrPreview() {
  const wrap = document.getElementById('qrPreview');
  if (!wrap) return;
  wrap.replaceChildren();

  if (!localSocialQrImage || !localSocialQrImage.url) {
    const empty = document.createElement('div');
    empty.className = 'preview-img preview-empty';
    empty.textContent = 'Nesetat';
    wrap.appendChild(empty);
    return;
  }

  const safeUrl = sanitizeImageSrc(localSocialQrImage.url);
  if (!safeUrl) return;
  const node = document.createElement('div');
  node.className = 'preview-img';
  const img = document.createElement('img');
  img.src = safeUrl;
  img.alt = 'preview-social-qr';

  const removeBtn = document.createElement('button');
  removeBtn.type = 'button';
  removeBtn.className = 'preview-remove-btn';
  removeBtn.setAttribute('aria-label', 'Șterge codul QR social din listă');
  removeBtn.textContent = '×';
  removeBtn.addEventListener('click', () => {
    if (localSocialQrImage?.type === 'local' && localSocialQrImage.previewUrl) {
      try {
        URL.revokeObjectURL(localSocialQrImage.previewUrl);
      } catch (e) {
        // Non-critical
      }
    }
    localSocialQrImage = null;
    setSocialQrDirtyState(true);
    renderLocalSocialQrPreview();
  });

  node.appendChild(img);
  node.appendChild(removeBtn);
  wrap.appendChild(node);
}

function handleBgUploadSelection(bgUploadEl) {
    const selected = Array.from(bgUploadEl.files || []);
    if (selected.length === 0) {
      // Android: unele galerii/aplicații „cloud” întorc selecția goală — altfel părea că nu se întâmplă nimic
      showSnackbar('Nu am primit nicio poză din galerie. Încearcă din nou sau alege poza din „Fișiere”.', 'error');
      return;
    }

    // „Înlocuiește” pe o poză existentă: prima poză aleasă îi ia locul
    if (bgReplaceIndex !== null && bgReplaceIndex >= 0 && bgReplaceIndex < localBgImages.length) {
      const file = selected[0];
      const old = localBgImages[bgReplaceIndex];
      if (old?.type === 'local' && old.previewUrl) {
        try { URL.revokeObjectURL(old.previewUrl); } catch (e) { /* non-critical */ }
      }
      const previewUrl = URL.createObjectURL(file);
      localBgImages[bgReplaceIndex] = { type: 'local', file, previewUrl, url: previewUrl };
      bgReplaceIndex = null;
      setBgDirtyState(true);
      renderLocalBgPreview();
      showSnackbar('Poza a fost înlocuită local. Apasă „Salvează” pentru a o păstra.', 'info');
      bgUploadEl.value = '';
      return;
    }
    bgReplaceIndex = null;

    const currentCount = localBgImages.length;
    const slots = Math.max(0, 5 - currentCount);
    if (slots === 0) {
      bgUploadEl.value = '';
      showSnackbar('Poți avea maxim 5 imagini în slideshow.', 'error');
      return;
    }

    const toAdd = selected.slice(0, slots);
    toAdd.forEach((file) => {
      const previewUrl = URL.createObjectURL(file);
      localBgImages.push({
        type: 'local',
        file,
        previewUrl,
        url: previewUrl
      });
    });

    if (selected.length > slots) {
      showSnackbar(`Ai depășit limita de 5 imagini (adăugate: ${slots}).`, 'error');
      logger.warn('[Settings] Extra background images ignored due to 5-item limit');
    }

    setBgDirtyState(true);
    renderLocalBgPreview();
    showSnackbar('Pozele au fost adăugate local. Apasă „Salvează” pentru a le păstra.', 'info');
    bgUploadEl.value = '';
}

function bindBgUploadInput() {
  const bgUploadEl = $('bgUpload');
  if (!bgUploadEl || bgUploadEl.dataset.boundUnsavedPreview === '1') return;
  bgUploadEl.addEventListener('change', () => handleBgUploadSelection(bgUploadEl));
  bgUploadEl.dataset.boundUnsavedPreview = '1';
  // „Adaugă poze” anulează un „Înlocuiește” abandonat (galerie închisă fără alegere)
  document.addEventListener('click', (e) => {
    if (e.target?.closest?.('[data-action="triggerFileInput"][data-arg="bgUpload"]')) bgReplaceIndex = null;
  }, true);
}

function handleConfirmBgSelection(el) {
    const file = el.files && el.files[0];
    if (!file) {
      renderPreview('confirmBgPreview', currentSettings?.confirmBgURL || null);
      return;
    }
    let previewUrl = '';
    try {
      previewUrl = URL.createObjectURL(file);
    } catch (_) {
      previewUrl = '';
    }
    renderPreview('confirmBgPreview', previewUrl || null);
    showSnackbar('Poza a fost aleasă. Apasă „Salvează” pentru a o păstra.', 'info');
}

function bindConfirmBgUploadInput() {
  const el = $('confirmBgUpload');
  if (!el || el.dataset.boundLocalPreview === '1') return;
  el.addEventListener('change', () => handleConfirmBgSelection(el));
  el.dataset.boundLocalPreview = '1';
}

function bindLocationLogoUploadInput() {
  const uploadEl = $('locationLogoUpload');
  if (!uploadEl || uploadEl.dataset.boundUnsavedPreview === '1') return;
  uploadEl.addEventListener('change', () => {
    const file = uploadEl.files?.[0];
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    if (localLocationLogoImage?.type === 'local' && localLocationLogoImage.previewUrl) {
      try {
        URL.revokeObjectURL(localLocationLogoImage.previewUrl);
      } catch (e) {
        // Non-critical
      }
    }
    localLocationLogoImage = {
      type: 'local',
      file,
      previewUrl,
      url: previewUrl
    };
    setLocationLogoDirtyState(true);
    renderLocalLocationLogoPreview();
    showSnackbar('Logo adăugat local. Apasă „Salvează”.', 'info');
    uploadEl.value = '';
  });
  uploadEl.dataset.boundUnsavedPreview = '1';
}

function bindLocationHoursUploadInput() {
  const uploadEl = $('locationHoursUpload');
  if (!uploadEl || uploadEl.dataset.boundUnsavedPreview === '1') return;
  uploadEl.addEventListener('change', () => {
    const file = uploadEl.files?.[0];
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    if (localLocationHoursImage?.type === 'local' && localLocationHoursImage.previewUrl) {
      try {
        URL.revokeObjectURL(localLocationHoursImage.previewUrl);
      } catch (e) {
        // Non-critical
      }
    }
    localLocationHoursImage = {
      type: 'local',
      file,
      previewUrl,
      url: previewUrl
    };
    setLocationHoursDirtyState(true);
    renderLocalLocationHoursPreview();
    showSnackbar('Imaginea a fost adăugată local. Apasă „Salvează”.', 'info');
    uploadEl.value = '';
  });
  uploadEl.dataset.boundUnsavedPreview = '1';
}

function bindFeedbackQrUploadInput() {
  const feedbackQrUploadEl = $('feedbackQrUpload');
  if (!feedbackQrUploadEl || feedbackQrUploadEl.dataset.boundUnsavedPreview === '1') return;
  feedbackQrUploadEl.addEventListener('change', () => {
    const file = feedbackQrUploadEl.files?.[0];
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    if (localFeedbackQrImage?.type === 'local' && localFeedbackQrImage.previewUrl) {
      try {
        URL.revokeObjectURL(localFeedbackQrImage.previewUrl);
      } catch (e) {
        // Non-critical
      }
    }
    localFeedbackQrImage = {
      type: 'local',
      file,
      previewUrl,
      url: previewUrl
    };
    setFeedbackQrDirtyState(true);
    renderLocalFeedbackQrPreview();
    showSnackbar('Codul QR pentru feedback a fost adăugat local. Apasă „Salvează”.', 'info');
    feedbackQrUploadEl.value = '';
  });
  feedbackQrUploadEl.dataset.boundUnsavedPreview = '1';
}

function bindSocialQrUploadInput() {
  const socialQrUploadEl = $('qrUpload');
  if (!socialQrUploadEl || socialQrUploadEl.dataset.boundUnsavedPreview === '1') return;
  socialQrUploadEl.addEventListener('change', () => {
    const file = socialQrUploadEl.files?.[0];
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    if (localSocialQrImage?.type === 'local' && localSocialQrImage.previewUrl) {
      try {
        URL.revokeObjectURL(localSocialQrImage.previewUrl);
      } catch (e) {
        // Non-critical
      }
    }
    localSocialQrImage = {
      type: 'local',
      file,
      previewUrl,
      url: previewUrl
    };
    setSocialQrDirtyState(true);
    renderLocalSocialQrPreview();
    showSnackbar('Codul QR pentru social media a fost adăugat local. Apasă „Salvează”.', 'info');
    socialQrUploadEl.value = '';
  });
  socialQrUploadEl.dataset.boundUnsavedPreview = '1';
}

export function getCurrentSettings() {
  // ✅ FIX: Încarcă din localStorage dacă currentSettings e null și cache-ul e valid
  if (!currentSettings) {
    try {
      const stored = localStorage.getItem(SETTINGS_CACHE_KEY);
      const timestamp = localStorage.getItem(SETTINGS_CACHE_TIMESTAMP_KEY);
      
      if (stored && timestamp) {
        const age = Date.now() - parseInt(timestamp, 10);
        const isOnline = navigator.onLine;
        
        // ✅ FIX: TTL mai mare (7 zile) SAU păstrează cache dacă ești offline
        // Nu șterge cache-ul dacă ești offline - îl păstrezi până revine netul
        if (age < SETTINGS_LOCALSTORAGE_TTL_MS || !isOnline) {
          currentSettings = JSON.parse(stored);
          logger.log('[Settings] Loaded from localStorage cache (age:', Math.round(age / 1000), 's, online:', isOnline, ')');
        } else {
          // Cache expirat ȘI suntem online - putem șterge (putem reîncărca din Firestore)
          localStorage.removeItem(SETTINGS_CACHE_KEY);
          localStorage.removeItem(SETTINGS_CACHE_TIMESTAMP_KEY);
          logger.log('[Settings] localStorage cache expired and online, cleared');
        }
      }
    } catch (err) {
      // Non-critical: dacă localStorage eșuează (quota exceeded, disabled), continuă cu null
      logger.warn('[Settings] Failed to load from localStorage:', err);
    }
  }
  return currentSettings;
}

export function setCurrentSettings(settings) {
  currentSettings = settings;
  
  // ✅ FIX: Persistă în localStorage pentru offline support
  // Dacă ești offline și faci refresh, setările vor fi disponibile din localStorage
  try {
    if (settings) {
      localStorage.setItem(SETTINGS_CACHE_KEY, JSON.stringify(settings));
      localStorage.setItem(SETTINGS_CACHE_TIMESTAMP_KEY, Date.now().toString());
      logger.log('[Settings] Persisted to localStorage');
    } else {
      // Dacă settings e null, șterge cache-ul
      localStorage.removeItem(SETTINGS_CACHE_KEY);
      localStorage.removeItem(SETTINGS_CACHE_TIMESTAMP_KEY);
    }
  } catch (err) {
    // Non-critical: dacă localStorage e plin sau disabled, continuă fără persistence
    // (doar memory cache va fi disponibil, dar e mai bine decât nimic)
    logger.warn('[Settings] Failed to persist to localStorage (non-critical):', err);
  }
}

export function resetCurrentSettings() {
  currentSettings = null;
  lastSettingsLoadAt = 0;
  
  // ✅ FIX: Șterge și din localStorage când resetezi
  try {
    localStorage.removeItem(SETTINGS_CACHE_KEY);
    localStorage.removeItem(SETTINGS_CACHE_TIMESTAMP_KEY);
  } catch (err) {
    // Non-critical
    logger.warn('[Settings] Failed to clear localStorage:', err);
  }
}

// Global state for settings listener (Mod Client realtime monitoring)
let settingsUnsub = null;
let currentListenerUid = null; // ✅ Track current listener UID
let currentStatusCallback = null; // ✅ Track current callback

// ✅ Start realtime listener for settings (used in Mod Client)
export function startClientSettingsListener(uid, onStatusChange) {
  // ✅ FIX: Guard against duplicate listeners for same UID
  if (settingsUnsub && currentListenerUid === uid) {
    // Already listening for this UID - just update callback if different
    if (currentStatusCallback !== onStatusChange) {
      currentStatusCallback = onStatusChange;
      logger.log('[Settings] Listener already active for UID, updated callback');
    }
    return; // Don't create duplicate listener
  }
  
  // ✅ FIX: If listening for different UID, stop old listener first
  if (settingsUnsub && currentListenerUid !== uid) {
    stopClientSettingsListener();
  }
  
  // ✅ FIX: Track current listener state
  currentListenerUid = uid;
  currentStatusCallback = onStatusChange;
  
  try {
    // ✅ FIX: Folosește același stil ca restul proiectului (db.collection().doc())
    const ref = db.collection('settings').doc(uid);
    settingsUnsub = ref.onSnapshot(async (snap) => {
      // ✅ DEBUG A: Get current page ID and device for logging
      const currentPage = document.querySelector('.page.visible')?.id || 'unknown';
      const device = navigator.userAgent.includes('Mobile') ? 'mobile' : 'desktop';
      const newSettings = snap.data() || {};
      const ts = new Date().toISOString();
      
      // ✅ DEBUG A: Log snapshot data with {uid, device, pageId, campaignStatus, totalTries, remainingTries, updatedAt, ts}
      console.log('[SETTINGS SNAP]', {
        uid: uid,
        device: device,
        pageId: currentPage,
        campaignStatus: newSettings.campaignStatus,
        totalTries: newSettings.totalTries,
        remainingTries: newSettings.remainingTries,
        updatedAt: newSettings.updatedAt,
        ts: ts
      });
      
      logger.log('[Settings] Listener snapshot:', {
        uid: uid,
        device: device,
        pageId: currentPage,
        online: navigator.onLine,
        campaignStatus: newSettings.campaignStatus,
        totalTries: newSettings.totalTries,
        remainingTries: newSettings.remainingTries,
        updatedAt: newSettings.updatedAt,
        source: 'Firestore',
        timestamp: ts
      });
      
      if (!snap.exists) {
        logger.warn('[Settings] Realtime listener: settings document does not exist');
        return;
      }
      
      // ✅ FIX 3: Validate field names (migrate old field names)
      const validatedSettings = validateSettingsFields(newSettings);
      
      const newStatus = validatedSettings?.campaignStatus || 'draft';
      const oldStatus = currentSettings?.campaignStatus || 'draft';
      
      // ✅ DEBUG A: Log separately when newStatus !== oldStatus
      if (newStatus !== oldStatus) {
        console.log('[STATUS CHANGE]', oldStatus, '->', newStatus, {
          device: device,
          pageId: currentPage,
          ts: ts
        });
        logger.log('[STATUS] campaignStatus changed', oldStatus, '->', newStatus, {
          device: device,
          pageId: currentPage,
          timestamp: ts
        });
      }
      
      const existingSettings = getCurrentSettings() || {};
      
      // ✅ FIX 1: Log existingSettings before merge
      console.log('[SETTINGS BEFORE MERGE]', {
        localTotalTries: existingSettings?.totalTries,
        localRemainingTries: existingSettings?.remainingTries,
        remoteTotalTries: validatedSettings?.totalTries,
        remoteRemainingTries: validatedSettings?.remainingTries
      });
      
      // ✅ FIX P1: Folosește IndexedDB pentru pending count (atomic, multi-tab safe)
      const currentUser = getCurrentUser();
      let pending = 0;
      if (currentUser) {
        try {
          pending = await getPendingPlaysCount(currentUser.uid);
        } catch (e) {
          logger.warn('[Settings] Failed to get pending plays count:', e);
          pending = Number(existingSettings?.offlinePlaysPending || 0);
        }
      }
      
      // ✅ FIX: Migrate prizes to have IDs if they don't have them
      const migratedPrizes = migratePrizesToHaveIds(validatedSettings?.prizes || []);
      
      // ✅ FIX 2: When online and pending==0, Firestore is SINGLE source of truth for BOTH totalTries and remainingTries
      if (navigator.onLine && pending === 0) {
        // Firestore = source of truth - folosește întotdeauna remote pentru TOATE câmpurile
        currentSettings = {
          ...validatedSettings,
          prizes: migratedPrizes,
          offlinePlaysPending: pending
        };
        
        // ✅ FIX 1: Log after merge
        console.log('[SETTINGS AFTER MERGE]', {
          finalTotalTries: currentSettings.totalTries,
          finalRemainingTries: currentSettings.remainingTries,
          source: 'Firestore (online, no pending)'
        });
        
        logger.log('[Settings] Listener: Using remote settings (online, no pending) - totalTries:', validatedSettings?.totalTries, 'remainingTries:', validatedSettings?.remainingTries, 'pending:', pending);
      } else if (pending > 0) {
        // ✅ FIX 2: If pending>0, do NOT overwrite totalTries from local. Only remainingTries may be min(remote, local)
        // Keep totalTries from remote (Firestore), only adjust remainingTries
        const remoteRemaining = Number(validatedSettings?.remainingTries || 0);
        const localRemaining = Number(existingSettings?.remainingTries || 0);
        const finalRemaining = Math.min(remoteRemaining, localRemaining);
        
        currentSettings = {
          ...validatedSettings, // ✅ Use remote totalTries
          remainingTries: finalRemaining, // ✅ Only remainingTries uses min()
          prizes: migratedPrizes,
          offlinePlaysPending: pending
        };
        
        // ✅ FIX 1: Log after merge
        console.log('[SETTINGS AFTER MERGE]', {
          finalTotalTries: currentSettings.totalTries,
          finalRemainingTries: currentSettings.remainingTries,
          source: 'Firestore (pending exists)'
        });
        
        logger.log('[Settings] Listener: Using merged settings (pending exists) - totalTries:', validatedSettings?.totalTries, 'remainingTries:', finalRemaining, 'pending:', pending);
      } else {
        // Offline fallback
        currentSettings = {
          ...validatedSettings,
          prizes: migratedPrizes,
          offlinePlaysPending: pending
        };
        
        logger.log('[Settings] Listener: Using remote settings (offline fallback)');
      }
      
      // Setările de fidelizare și „bon obligatoriu” NU stau în settings/{uid} (vin din merchants/{uid}/loyaltySettings,
      // respectiv advancedClaims, la loadSettings). Documentul din snapshot nu le are, iar validateSettingsFields
      // pune în loc „fidelizare inactivă”: fără păstrarea lor, orice snapshot (ex. după o salvare din alt tab) arăta
      // modulul ca oprit, iar salvarea textelor de fidelizare trimitea active:false și dezactiva programul.
      if (newSettings.loyaltySettings === undefined && existingSettings && existingSettings.loyaltySettings && typeof existingSettings.loyaltySettings === 'object') {
        currentSettings.loyaltySettings = existingSettings.loyaltySettings;
      }
      if (newSettings.requireReceiptNumber === undefined && existingSettings && typeof existingSettings.requireReceiptNumber === 'boolean') {
        currentSettings.requireReceiptNumber = existingSettings.requireReceiptNumber;
      }

      setCurrentSettings(currentSettings); // Persist to localStorage

      // Joy Assistant: obiectul de setări a fost înlocuit — preview-ul își reaplică patch-ul
      try {
        document.dispatchEvent(new CustomEvent('sj:settings-refreshed'));
      } catch (_) { /* ignore */ }

      // ✅ FIX 1: Log getCurrentSettings() after setCurrentSettings
      const verifySettings = getCurrentSettings();
      console.log('[SETTINGS AFTER SET]', {
        totalTries: verifySettings?.totalTries,
        remainingTries: verifySettings?.remainingTries
      });
      
      // ✅ FIX: Update UI when settings change
      if (typeof window !== 'undefined' && window.updateSectionSummaries) {
        window.updateSectionSummaries();
      } else {
        import('../ui/admin-collapsible.js?v=83.21').then(module => {
          if (module.updateSectionSummaries) {
            module.updateSectionSummaries();
          }
        }).catch(() => {});
      }
      
      // Update tries counter if function exists
      if (typeof window.updateTriesCounter === 'function') {
        window.updateTriesCounter();
      }

      import('../ui/account.js?v=83.21').then((m) => {
        if (m.updateAdminPageBrand) m.updateAdminPageBrand();
      }).catch(() => {});
      
      // ✅ FIX 5: Ensure settings listener calls currentStatusCallback(newStatus, oldStatus)
      // This ensures UI updates immediately when status changes
      if (newStatus !== oldStatus && currentStatusCallback) {
        // ✅ DEBUG B: Log before calling callback
        const currentPage = document.querySelector('.page.visible')?.id || 'unknown';
        const device = navigator.userAgent.includes('Mobile') ? 'mobile' : 'desktop';
        console.log('[CALLBACK] about to call', {
          oldStatus: oldStatus,
          newStatus: newStatus,
          device: device,
          pageId: currentPage,
          callbackExists: !!currentStatusCallback
        });
        logger.log('[Settings] Status changed - calling handleCampaignStatusChange:', oldStatus, '->', newStatus, {
          device: device,
          pageId: currentPage
        });
        
        // ✅ DEBUG B: Try/catch around callback to log errors
        try {
          currentStatusCallback(newStatus, oldStatus);
          console.log('[CALLBACK] called successfully');
        } catch (e) {
          console.error('[CALLBACK] ERROR:', e);
          logger.error('[Settings] Error in status callback:', e);
        }
      }
    }, (err) => {
      if (logFirestoreAccessDeniedOnce('Settings', err)) {
        stopClientSettingsListener();
        return;
      }
      logger.error('[Settings] Realtime listener error:', err);
    });
    
    logger.log('[Settings] Realtime listener started for UID:', uid);
  } catch (e) {
    logger.error('[Settings] Failed to start realtime listener:', e);
    currentListenerUid = null;
    currentStatusCallback = null;
  }
}

// ✅ Stop realtime listener
export function stopClientSettingsListener() {
  if (settingsUnsub) {
    try {
      settingsUnsub();
      logger.log('[Settings] Realtime listener stopped for UID:', currentListenerUid);
    } catch (e) {
      logger.error('[Settings] Error stopping realtime listener:', e);
    }
    settingsUnsub = null;
    currentListenerUid = null;
    currentStatusCallback = null;
  }
}

// ----- Public QR: realtime mirror publicSites/{slug} -----
let publicSiteUnsub = null;
let publicSiteListenerSlug = null;
let publicSiteStatusCallback = null;

export function stopPublicSiteListener() {
  if (publicSiteUnsub) {
    try {
      publicSiteUnsub();
      logger.log('[Settings] Public site listener stopped:', publicSiteListenerSlug);
    } catch (e) {
      logger.error('[Settings] Error stopping public site listener:', e);
    }
    publicSiteUnsub = null;
    publicSiteListenerSlug = null;
    publicSiteStatusCallback = null;
  }
}

export function startPublicSiteListener(slug, onStatusChange) {
  if (!slug) return;

  if (publicSiteUnsub && publicSiteListenerSlug === slug) {
    if (onStatusChange && publicSiteStatusCallback !== onStatusChange) {
      publicSiteStatusCallback = onStatusChange;
    }
    return;
  }

  stopPublicSiteListener();
  publicSiteListenerSlug = slug;
  publicSiteStatusCallback = onStatusChange;

  try {
    const ref = db.collection('publicSites').doc(slug);
    const applyPublicSiteSnapshot = (raw) => {
      const existingBefore = getCurrentSettings() || {};
      const oldStatus = existingBefore.campaignStatus || 'draft';

      const validatedSettings = validateSettingsFields({ ...raw });
      const migratedPrizes = migratePrizesToHaveIds(validatedSettings?.prizes || []);

      currentSettings = {
        ...validatedSettings,
        prizes: migratedPrizes,
        offlinePlaysPending: 0
      };

      setCurrentSettings(currentSettings);

      if (
        typeof document !== 'undefined' &&
        /^\/m\/[a-z0-9-]{3,48}\/?$/i.test(String(window.location.pathname || ''))
      ) {
        import('../utils/public-bootstrap-branding.js?v=83.21')
          .then(({ applyPublicBootstrapLogo, applyClientPublicBrandLogo }) => {
            applyClientPublicBrandLogo(currentSettings);
            if (!document.body.classList.contains('sj-public-ui-ready')) {
              applyPublicBootstrapLogo(currentSettings, { minVisibleMs: 600 });
            }
          })
          .catch(() => {});
      }

      if (typeof window !== 'undefined' && typeof window.updateTriesCounter === 'function') {
        window.updateTriesCounter();
      }

      if (typeof document !== 'undefined') {
        const cp = document.getElementById('clientPage');
        if (cp?.classList.contains('visible')) {
          import('../ui/slideshow.js?v=83.21').then((m) => m.applyClientUI()).catch(() => {});
        }
      }

      const newStatus = validatedSettings?.campaignStatus || 'draft';
      if (newStatus !== oldStatus && publicSiteStatusCallback) {
        try {
          publicSiteStatusCallback(newStatus, oldStatus);
        } catch (e) {
          logger.error('[PublicSite] Status callback error:', e);
        }
      }
    };

    publicSiteUnsub = ref.onSnapshot(
      (snap) => {
        if (!snap.exists) {
          logger.warn('[PublicSite] Snapshot: document missing');
          return;
        }

        if (snap.metadata?.fromCache && navigator.onLine) {
          try {
            logger.log('[PublicSite] cache snapshot — server refresh start');
          } catch (_) {}
          withTimeoutMs(ref.get({ source: 'server' }), PUBLIC_SITE_SERVER_REFRESH_MS)
            .then((serverSnap) => {
              if (serverSnap.exists) {
                try {
                  logger.log('[PublicSite] server refresh OK');
                } catch (_) {}
                applyPublicSiteSnapshot(serverSnap.data() || {});
              } else {
                applyPublicSiteSnapshot(snap.data() || {});
              }
            })
            .catch(() => {
              try {
                logger.warn('[PublicSite] server refresh timeout or failed — using cache snapshot');
              } catch (_) {}
              applyPublicSiteSnapshot(snap.data() || {});
            });
          return;
        }

        applyPublicSiteSnapshot(snap.data() || {});
      },
      (err) => {
        logger.error('[PublicSite] Listener error:', err);
      }
    );

    logger.log('[Settings] Public site listener started for slug:', slug);
  } catch (e) {
    logger.error('[Settings] Failed to start public site listener:', e);
    publicSiteListenerSlug = null;
    publicSiteStatusCallback = null;
  }
}

// ✅ FIX 3: Search for field name mismatches - add validation
export function validateSettingsFields(settings) {
  // ✅ FIX 3: Ensure exact field names: totalTries and remainingTries
  const validated = { ...settings };
  
  // Map old field names to new ones (if any exist)
  if (validated.triesTotal !== undefined && validated.totalTries === undefined) {
    validated.totalTries = validated.triesTotal;
    delete validated.triesTotal;
  }
  if (validated.triesRemaining !== undefined && validated.remainingTries === undefined) {
    validated.remainingTries = validated.triesRemaining;
    delete validated.triesRemaining;
  }
  if (validated.totalAttempts !== undefined && validated.totalTries === undefined) {
    validated.totalTries = validated.totalAttempts;
    delete validated.totalAttempts;
  }
  if (validated.remainingAttempts !== undefined && validated.remainingTries === undefined) {
    validated.remainingTries = validated.remainingAttempts;
    delete validated.remainingAttempts;
  }

  validated.menuLabel = String(validated.menuLabel || 'Meniu').trim().slice(0, 40) || 'Meniu';
  validated.menuImages = Array.isArray(validated.menuImages)
    ? validated.menuImages
        .map((u) => String(u || '').trim())
        .filter(Boolean)
        .slice(0, 15)
    : [];

  if (!validated.clientPromoGallery || typeof validated.clientPromoGallery !== 'object') {
    validated.clientPromoGallery = { enabled: false, title: '', images: [] };
  } else {
    const g = validated.clientPromoGallery;
    validated.clientPromoGallery = {
      enabled: g.enabled === true,
      title: String(g.title || '')
        .trim()
        .slice(0, 80),
      images: Array.isArray(g.images)
        ? g.images
            .map((u) => String(u || '').trim())
            .filter(Boolean)
            .slice(0, 10)
        : [],
      labels: Array.isArray(g.labels) ? g.labels.slice(0, 10).map((l) => String(l || '').trim().slice(0, 40)) : [],
      links: Array.isArray(g.links) ? g.links.slice(0, 10).map((l) => String(l || '').trim().slice(0, 500)) : []
    };
  }

  if (!validated.loyaltySettings || typeof validated.loyaltySettings !== 'object') {
    validated.loyaltySettings = {
      active: false,
      title: 'Fidelizare',
      description: 'Înregistrează achizițiile făcute la noi și primește recompensa.',
      buttonText: 'Înregistrează achiziția',
      requiredPurchases: 7,
      rewardText: 'La 7 produse cumpărate, primești unul gratuit.',
      promoImageUrl: null,
      startDate: '',
      endDate: ''
    };
  } else {
    const ls = validated.loyaltySettings;
    const req = parseInt(ls.requiredPurchases, 10);
    validated.loyaltySettings = {
      active: ls.active === true,
      title: String(ls.title || 'Fidelizare').trim().slice(0, 80),
      description: String(
        ls.description || 'Înregistrează achizițiile făcute la noi și primește recompensa.'
      )
        .trim()
        .slice(0, 500),
      buttonText: String(ls.buttonText || 'Înregistrează achiziția').trim().slice(0, 60),
      requiredPurchases: Number.isFinite(req) && req >= 1 && req <= 99 ? req : 7,
      rewardText: String(ls.rewardText || 'La 7 produse cumpărate, primești unul gratuit.')
        .trim()
        .slice(0, 300),
      promoImageUrl: ls.promoImageUrl ? String(ls.promoImageUrl).trim().slice(0, 500) : null,
      startDate: String(ls.startDate || '').trim().slice(0, 10),
      endDate: String(ls.endDate || '').trim().slice(0, 10)
    };
    if (validated.campaignStatus === 'active') {
      validated.loyaltySettings.active = false;
    }
  }

  if (!validated.eventModule || typeof validated.eventModule !== 'object') {
    validated.eventModule = {
      enabled: false,
      buttonLabel: 'Descoperă povestea noastră',
      title: 'Povestea noastră',
      text: '',
      images: []
    };
  } else {
    const em = validated.eventModule;
    validated.eventModule = {
      enabled: em.enabled === true,
      buttonLabel: String(em.buttonLabel || 'Descoperă povestea noastră')
        .trim()
        .slice(0, 60),
      title: String(em.title || 'Povestea noastră')
        .trim()
        .slice(0, 80),
      text: String(em.text || '').trim().slice(0, 2000),
      images: Array.isArray(em.images)
        ? em.images
            .map((u) => String(u || '').trim())
            .filter(Boolean)
            .slice(0, 10)
        : [],
      eventDate: /^\d{4}-\d{2}-\d{2}$/.test(String(em.eventDate || '')) ? String(em.eventDate) : '',
      eventTime: /^\d{2}:\d{2}$/.test(String(em.eventTime || '')) ? String(em.eventTime) : ''
    };
  }

  validated.clientLayoutConfig = sanitizeClientLayoutConfig(validated.clientLayoutConfig);
  const templateStyles = ['standard', 'elegant', 'modern', 'dynamic', 'future', 'minimal', 'connect', 'alfa', 'custom', 'avva', 'afis', 'tabla', 'glass', 'cloud', 'cloud-light', 'rimga', 'ai-generated'];
  const rawTemplate = String(validated.clientTemplateStyle || 'cloud').trim().toLowerCase();
  validated.clientTemplateStyle = templateStyles.includes(rawTemplate) ? rawTemplate : 'cloud';
  const rawVariant = String(validated.clientTemplateAiVariant || '').trim().toLowerCase();
  validated.clientTemplateAiVariant =
    ['elegant', 'modern', 'dynamic', 'future', 'minimal', 'connect', 'alfa', 'custom', 'avva'].includes(rawVariant)
      ? rawVariant
      : null;
  const rawAccent = String(validated.customTemplateAccent || '').trim().toLowerCase();
  validated.customTemplateAccent = ['gold', 'rose', 'blue', 'emerald', 'silver', 'purple', 'orange'].includes(rawAccent)
    ? rawAccent
    : null;
  validated.communityEnabled = validated.communityEnabled === true;
  validated.communityMessage = String(validated.communityMessage || '').trim().slice(0, 220);
  validated.wifiEnabled = validated.wifiEnabled === true;
  validated.wifiSsid = String(validated.wifiSsid || '').trim().slice(0, 64);
  validated.wifiPassword = String(validated.wifiPassword || '').trim().slice(0, 128);
  validated.locationHoursEnabled = validated.locationHoursEnabled === true;
  validated.locationHoursImageURL = validated.locationHoursImageURL
    ? String(validated.locationHoursImageURL).trim().slice(0, 500)
    : null;
  validated.reservationLabel = String(validated.reservationLabel || 'Rezervare')
    .trim()
    .slice(0, 40) || 'Rezervare';
  validated.reservationEnabled = validated.reservationEnabled === true;
  validated.reservationPhone = String(validated.reservationPhone || '').trim().slice(0, 32);
  validated.reservationMessage = String(validated.reservationMessage || '').trim().slice(0, 220);
  validated.reservationWhatsapp = String(validated.reservationWhatsapp || '').trim().slice(0, 32);
  validated.reservationLink = /^https?:\/\//i.test(String(validated.reservationLink || '')) ? String(validated.reservationLink).trim().slice(0, 500) : '';
  validated.reservationLinkLabel = String(validated.reservationLinkLabel || '').trim().slice(0, 30);
  validated.locationMapEnabled = validated.locationMapEnabled === true;
  validated.locationMapName = String(validated.locationMapName || '').trim().slice(0, 80);
  validated.locationMapAddress = String(validated.locationMapAddress || '').trim().slice(0, 200);
  validated.locationMapUrl = validated.locationMapUrl
    ? String(validated.locationMapUrl).trim().slice(0, 2048)
    : '';
  validated.locationLogoURL = validated.locationLogoURL
    ? String(validated.locationLogoURL).trim().slice(0, 500)
    : null;
  validated.clientTagline = validated.clientTagline
    ? String(validated.clientTagline).trim().slice(0, 120)
    : null;

  const ratingRaw =
    validated.googleRating ??
    validated.clientReviewRating ??
    validated.feedbackRating ??
    validated.reviewRating;
  const countRaw =
    validated.googleReviewCount ??
    validated.clientReviewCount ??
    validated.feedbackReviewCount ??
    validated.reviewCount;
  const ratingNum = Number(String(ratingRaw ?? '').replace(',', '.').trim());
  const countNum = parseInt(String(countRaw ?? '').replace(/\D/g, ''), 10);
  validated.googleRating =
    Number.isFinite(ratingNum) && ratingNum > 0 && ratingNum <= 5
      ? Math.round(ratingNum * 10) / 10
      : null;
  validated.googleReviewCount =
    Number.isFinite(countNum) && countNum > 0 ? countNum : null;
  validated.clientReviewQuote = String(validated.clientReviewQuote || '').trim().slice(0, 120);
  validated.clientReviewHighlight = String(validated.clientReviewHighlight || '').trim().slice(0, 120);
  validated.clientReviewQuotes = Array.isArray(validated.clientReviewQuotes)
    ? validated.clientReviewQuotes
        .map((entry) => String(entry || '').trim().slice(0, 120))
        .filter(Boolean)
        .slice(0, 2)
    : [];
  validated.feedbackCardTitle = String(validated.feedbackCardTitle || 'Recenzie').trim().slice(0, 40) || 'Recenzie';

  if (validated.socialMedia && typeof validated.socialMedia === 'object') {
    const clean = {};
    for (const p of ['facebook', 'instagram', 'tiktok']) {
      const raw = validated.socialMedia[p] || {};
      clean[p] = {
        enabled: raw.enabled === true,
        link: String(raw.link || '').trim().slice(0, 2048),
        qrUrl: String(raw.qrUrl || '').trim()
      };
    }
    validated.socialMedia = clean;
  }

  return validated;
}

// ✅ FIX: Migrate existing prizes to have IDs (one-time migration)
export function migratePrizesToHaveIds(prizes) {
  if (!Array.isArray(prizes)) return prizes;
  
  return prizes.map((prize, index) => {
    // If prize already has an ID, keep it
    if (prize.id || prize.uid || prize.key) {
      return prize;
    }
    
    // Generate ID for prizes without one
    return {
      ...prize,
      id: `prize_migrated_${Date.now()}_${index}_${Math.random().toString(36).substr(2, 9)}`
    };
  });
}

/** Alias pentru bundle-uri / importuri vechi (ex. cache SW). */
export const migratePrizesToRuleIds = migratePrizesToHaveIds;

const CLIENT_TEMPLATE_STYLE_IDS = [
  'standard',
  'elegant',
  'modern',
  'dynamic',
  'future',
  'minimal',
  'connect',
  'alfa',
  'custom',
  'avva',
  'afis',
  'tabla',
  'glass',
  'cloud',
  'cloud-light',
  'rimga',
  'ai-generated'
];
const CLIENT_TEMPLATE_VARIANT_IDS = ['elegant', 'modern', 'dynamic', 'future', 'minimal', 'connect', 'alfa', 'custom', 'avva'];

/** Scriere directă Firestore pentru stil template (regulile permit merge pe settings/{uid}). */
export async function persistClientTemplateChoice(uid, patch = {}) {
  if (!uid) throw new Error('UID necesar pentru salvarea stilului');

  const rawStyle = String(patch.clientTemplateStyle || '').trim().toLowerCase();
  if (!CLIENT_TEMPLATE_STYLE_IDS.includes(rawStyle)) {
    throw new Error('Stil template invalid');
  }

  const rawVariant =
    patch.clientTemplateAiVariant == null
      ? null
      : String(patch.clientTemplateAiVariant).trim().toLowerCase();
  const clientTemplateAiVariant =
    rawVariant && CLIENT_TEMPLATE_VARIANT_IDS.includes(rawVariant) ? rawVariant : null;

  const data = {
    clientTemplateStyle: rawStyle,
    clientTemplateAiVariant: clientTemplateAiVariant
  };
  if (patch.clientLayoutConfig !== undefined) {
    data.clientLayoutConfig = sanitizeClientLayoutConfig(patch.clientLayoutConfig);
  }
  if (patch.buttonColor !== undefined) data.buttonColor = patch.buttonColor;
  if (patch.buttonText !== undefined) data.buttonText = patch.buttonText;
  if (patch.claimMessage !== undefined) data.claimMessage = patch.claimMessage;
  if (patch.customTemplateAccent !== undefined) {
    const accentIds = ['gold', 'rose', 'blue', 'emerald', 'silver', 'purple', 'orange'];
    const rawAccent = String(patch.customTemplateAccent || '').trim().toLowerCase();
    data.customTemplateAccent = accentIds.includes(rawAccent) ? rawAccent : null;
  } else if (rawStyle === 'custom' || rawStyle === 'cloud' || rawStyle === 'cloud-light' || rawStyle === 'rimga') {
    const curAccent = String((getCurrentSettings() || {}).customTemplateAccent || '').trim().toLowerCase();
    if (['gold', 'rose', 'blue', 'emerald', 'silver', 'purple', 'orange'].includes(curAccent)) {
      data.customTemplateAccent = curAccent;
    }
  }

  const merged = {
    ...(getCurrentSettings() || {}),
    ...data
  };
  setCurrentSettings(merged);

  let saved = false;
  try {
    const { saveCampaignSettingsBackend } = await import('./backend.js?v=83.21');
    await saveCampaignSettingsBackend(data);
    saved = true;
  } catch (cfErr) {
    logger.warn('[persistClientTemplateChoice] Cloud Function save failed, trying direct Firestore:', cfErr);
  }

  if (!saved) {
    try {
      const directPayload = { ...data };
      if (firebaseRef?.firestore?.FieldValue?.serverTimestamp) {
        directPayload.updatedAt = firebaseRef.firestore.FieldValue.serverTimestamp();
      }
      await db.collection('settings').doc(uid).set(directPayload, { merge: true });
      saved = true;
    } catch (directErr) {
      logger.warn('[persistClientTemplateChoice] direct Firestore failed:', directErr);
      const blocked =
        String(directErr?.message || '').toLowerCase().includes('blocked') ||
        directErr?.code === 'permission-denied';
      throw new Error(
        blocked
          ? 'Salvarea a fost blocată (ad-blocker sau extensie browser). Permite app.spressjoy.ro și încearcă din nou.'
          : 'Nu am putut salva stilul. Verifică conexiunea și încearcă din nou.'
      );
    }
  }

  const slug = String(merged.publicSlug || '').trim().toLowerCase();
  if (slug && navigator.onLine) {
    try {
      const { refreshPublicSiteBackend } = await import('./backend.js?v=83.21');
      await refreshPublicSiteBackend();
      logger.log('[persistClientTemplateChoice] publicSites synced for slug:', slug);
    } catch (refreshErr) {
      logger.warn('[persistClientTemplateChoice] refreshPublicSite failed (non-critical):', refreshErr);
    }
  }

  return merged;
}

/* ===== Settings load/save ===== */
export async function loadSettings(uid, force = false) {
  const now = Date.now();
  if (!force && (now - lastSettingsLoadAt) < SETTINGS_TTL_MS && currentSettings) {
    // Setările sunt proaspete, dar formularul de admin poate fi încă nepopulat/nelegat
    // (la logare, încărcarea se face înainte ca adminPage să fie vizibilă).
    if (adminUiPopulatedAt < lastSettingsLoadAt) populateAdminUiFromSettings();
    return;
  }

  // ✅ FIX: Dacă ești offline, folosește localStorage dacă există (nu suprascrie cu date vechi din Firestore)
  if (!navigator.onLine) {
    const cached = getCurrentSettings(); // Încarcă din localStorage dacă există
    if (cached && Object.keys(cached).length > 0) {
      currentSettings = cached;
      logger.log('[Settings] Offline - using cached settings from localStorage');
      return; // ✅ NU popula UI - doar setează currentSettings și iesi
    }
  }

  const ref = db.collection('settings').doc(uid);
  // Cele trei citiri pornesc ÎN PARALEL (înainte erau una după alta: setări → bon obligatoriu → fidelizare prin
  // funcție cu pornire la rece, ~3 drumuri la server pe 5G înainte să apară adminul).
  const advancedClaimsPromise = db.collection('advancedClaims').doc(uid).get().catch((e) => { logger.warn('[loadSettings] advancedClaims read failed:', e); return null; });
  const loyaltyPromise = (async () => {
    try {
      // citire directă (rapidă); funcția rămâne doar ca rezervă când documentul nu există încă
      const snapL = await db.doc(`merchants/${uid}/loyaltySettings/config`).get();
      if (snapL.exists) return snapL.data() || null;
    } catch (e) {
      logger.warn('[loadSettings] loyalty direct read failed:', e);
    }
    try {
      const { getLoyaltySettingsBackend } = await import('./backend.js?v=83.21');
      const { withTimeoutMs } = await import('../utils/with-timeout.js?v=83.21');
      const loyaltyRes = await withTimeoutMs(getLoyaltySettingsBackend(), 8000);
      return loyaltyRes?.settings || null;
    } catch (loyErr) {
      logger.warn('[loadSettings] loyalty settings load failed:', loyErr);
      return null;
    }
  })();
  const snap = await ref.get();
  // TTL-ul se măsoară de la sosirea datelor, nu de la pornirea cererii: pe o rețea lentă, showPage('adminPage')
  // venea imediat după logare și refăcea toată încărcarea doar pentru că cererea durase mai mult de 4 s.
  lastSettingsLoadAt = Date.now();

  if (snap.exists) {
    const remoteSettings = snap.data() || {};
    remoteSettings.prizes = remoteSettings.prizes || [];
    
    // ✅ FIX 3: Validate field names (migrate old field names)
    const validatedRemoteSettings = validateSettingsFields(remoteSettings);
    
    // ✅ FIX 1: Log snapshot data
    console.log('[SETTINGS SNAP]', {
      uid: uid,
      totalTries: validatedRemoteSettings.totalTries,
      remainingTries: validatedRemoteSettings.remainingTries,
      clientTemplateStyle: validatedRemoteSettings.clientTemplateStyle,
      updatedAt: validatedRemoteSettings.updatedAt,
      timestamp: new Date().toISOString()
    });
    
    // ✅ FIX: Migrate prizes to have IDs if they don't have them
    validatedRemoteSettings.prizes = migratePrizesToHaveIds(validatedRemoteSettings.prizes);

    // ✅ FIX 2: Salvează remoteRemainingBefore și remoteTotalTries ÎNAINTE de orice modificare
    const remoteRemainingBefore = Number(validatedRemoteSettings?.remainingTries || 0);
    const remoteTotalTries = Number(validatedRemoteSettings?.totalTries || 0);
    
    // ✅ FIX 2: Aplică delta offline DOAR pentru remainingTries, NU pentru totalTries
    const localSettings = getCurrentSettings();
    let needsFirestoreUpdate = false;
    
    if (localSettings && navigator.onLine) {
      const pending = Number(localSettings?.offlinePlaysPending || 0);
      if (pending > 0 && Number.isFinite(pending) && Number.isFinite(remoteRemainingBefore)) {
        // ✅ FIX 2: Apply pending ONLY to remainingTries, keep totalTries from remote
        validatedRemoteSettings.remainingTries = Math.max(0, remoteRemainingBefore - pending);
        validatedRemoteSettings.offlinePlaysPending = 0;
        needsFirestoreUpdate = true;
        
        logger.log('[Settings] Applied offline delta - pending:', pending, 
                   'remoteRemainingBefore:', remoteRemainingBefore, 'final:', validatedRemoteSettings.remainingTries);
      }
      
      // ✅ FIX 2: Backup min(local, remote) ONLY for remainingTries, NOT for totalTries
      const localRemaining = Number(localSettings?.remainingTries);
      if (Number.isFinite(localRemaining) && Number.isFinite(remoteRemainingBefore)) {
        const minValue = Math.min(localRemaining, remoteRemainingBefore);
        if (minValue < validatedRemoteSettings.remainingTries) {
          validatedRemoteSettings.remainingTries = Math.max(0, minValue);
          logger.log('[Settings] Merged remainingTries (backup) - local:', localRemaining, 
                     'remoteBefore:', remoteRemainingBefore, 'final:', validatedRemoteSettings.remainingTries);
        }
      }
      
      // ✅ FIX 2: NEVER overwrite totalTries from local - always use remote
      // totalTries is set from validatedRemoteSettings above, no merge needed
    }

    // ✅ FIX 2: Ensure totalTries comes from remote, not local
    currentSettings = {
      ...validatedRemoteSettings,
      totalTries: remoteTotalTries, // ✅ Explicitly use remote totalTries
      prizes: validatedRemoteSettings.prizes
    };
    
    // ✅ FIX 1: Log after merge
    console.log('[SETTINGS AFTER LOAD]', {
      finalTotalTries: currentSettings.totalTries,
      finalRemainingTries: currentSettings.remainingTries,
      source: 'loadSettings'
    });
    
    // ✅ FIX: Dacă am aplicat pending, persistă în Firestore imediat (ca să nu revină la refresh)
    if (needsFirestoreUpdate && navigator.onLine) {
      try {
        const { saveCampaignSettingsBackend } = await import('./backend.js?v=83.21');
        // Trimite doar remainingTries și offlinePlaysPending pentru update rapid
        await saveCampaignSettingsBackend({
          remainingTries: validatedRemoteSettings.remainingTries,
          offlinePlaysPending: 0
        });
        logger.log('[Settings] Synced offline delta to Firestore - remainingTries:', validatedRemoteSettings.remainingTries);
      } catch (err) {
        // Non-critical: dacă sync eșuează, datele sunt deja în localStorage și vor fi încercate din nou la următorul loadSettings
        logger.warn('[Settings] Failed to sync offline delta to Firestore (will retry on next load):', err);
      }
    }

    // ✅ MIGRARE: dacă exista adminPass (clar) și nu exista hash, îl transformăm 1x
    if (currentSettings?.adminPass && !currentSettings?.adminPassHash) {
      try {
        const h = await sha256(currentSettings.adminPass);
        // ❌ REMOVED: Write direct - migration should be done via Cloud Function or ignored
        // await db.collection('settings').doc(uid).set(updateData, { merge: true });
        
        // Just update in memory - migration will be handled by backend on next save
        currentSettings.adminPassHash = h;
        delete currentSettings.adminPass;
        logger.log('[loadSettings] Password migrated to hash in memory for user:', uid);
        logger.warn('[loadSettings] Note: Migration write skipped - will be saved via Cloud Function on next settings update');
      } catch (e) {
        // Log migration error but don't block - user can still use app
        logger.error('[loadSettings] Error migrating password to hash:', e);
        // Keep old password in memory but don't save it back
        if (currentSettings.adminPass) {
          delete currentSettings.adminPass;
        }
      }
    }
    // Revendicări avansate: număr bon (nu e în settings/, e în advancedClaims/) — citirea a pornit deja mai sus
    try {
      const advSnap = await advancedClaimsPromise;
      if (advSnap && advSnap.exists) {
        const ad = advSnap.data() || {};
        currentSettings.requireReceiptNumber = ad.requireReceiptNumber === true;
      } else {
        currentSettings.requireReceiptNumber = false;
      }
    } catch (e) {
      logger.warn('[loadSettings] advancedClaims merge failed:', e);
      currentSettings.requireReceiptNumber = false;
    }
    const loyaltyLoaded = await loyaltyPromise;
    if (loyaltyLoaded) {
      currentSettings.loyaltySettings = loyaltyLoaded;
    }
    // ✅ FIX: Persistă în localStorage după încărcare din Firestore
    setCurrentSettings(currentSettings);
    
    // ✅ FIX 1: Log getCurrentSettings() after setCurrentSettings
    const verifySettings = getCurrentSettings();
    console.log('[SETTINGS AFTER SET]', {
      totalTries: verifySettings?.totalTries,
      remainingTries: verifySettings?.remainingTries
    });
  } else {
    // Document doesn't exist - don't create it here, it should be created via initSettings Cloud Function
    // Just set empty defaults for UI
    currentSettings = {
      totalTries: 0,
      remainingTries: 0,
      buttonText: 'Apasă!',
      buttonColor: '#43c047',
      adminPassHash: '',
      claimMessage: '',
      companyName: '',
      cui: '',
      operatorEmail: '',
      bgURL: null,
      bgURLs: [],
      qrURL: null,
      menuQrURL: null,
      confirmBgURL: null,
      slideDuration: 5,
      prizes: [],
      defaultPrizeMsg: 'Ne pare rău, nu ai câștigat!',
      defaultPrizeImg: null,
      feedbackEnabled: false,
      feedbackQrURL: null,
      feedbackButtonLabel: '',
      feedbackCardTitle: 'Recenzie',
      feedbackTitle: 'Îți mulțumim pentru vizită!',
      feedbackSubtext: 'Scanează codul pentru a ne lăsa o părere',
      socialCardHint: 'Urmărește-ne pe rețelele sociale',
      clientSocialUrl: '',
      socialMedia: {
        facebook: { enabled: false, link: '', qrUrl: '' },
        instagram: { enabled: false, link: '', qrUrl: '' },
        tiktok: { enabled: false, link: '', qrUrl: '' }
      },
      googleRating: null,
      googleReviewCount: null,
      clientReviewQuote: '',
      clientReviewHighlight: '',
      clientReviewQuotes: [],
      clientFeedbackUrl: '',
      confetti: 'on',
      playsTotal: 0,
      campaignStatus: 'draft',
      startedAt: null,
      closedAt: null,
      menuUrl: '',
      menuLabel: 'Meniu',
      menuImages: [],
      menuEnabled: false,
      communityEnabled: false,
      communityMessage: '',
      wifiEnabled: false,
      wifiSsid: '',
      wifiPassword: '',
      locationHoursEnabled: false,
      locationHoursImageURL: null,
      reservationLabel: 'Rezervare',
      reservationEnabled: false,
      reservationPhone: '',
      reservationMessage: '',
      locationMapEnabled: false,
      locationMapName: '',
      locationMapAddress: '',
      locationMapUrl: '',
      locationLogoURL: null,
      clientTagline: null,
      clientPromoGallery: { enabled: false, title: '', images: [] },
      eventModule: {
        enabled: false,
        buttonLabel: 'Descoperă povestea noastră',
        title: 'Povestea noastră',
        text: '',
        images: []
      },
      clientLayoutConfig: sanitizeClientLayoutConfig(null),
      clientTemplateStyle: 'modern',
      clientTemplateAiVariant: null
    };
    // ❌ REMOVED: await ref.set(currentSettings);
    // Document should be created via initSettings Cloud Function, not here
    // ✅ FIX: Persistă și defaults în localStorage
    setCurrentSettings(currentSettings);
    logger.warn('[loadSettings] Settings document does not exist for user:', uid);
  }

  // Populează UI doar dacă suntem pe adminPage (vezi populateAdminUiFromSettings).
  populateAdminUiFromSettings();
}

/** Populează și leagă formularul de admin din `currentSettings` (câmpuri, poze, galerii, butoane).
 *  Separat de loadSettings: la logare, loadSettings rulează ÎNAINTE ca adminPage să fie vizibilă, iar
 *  apelul următor (din showPage) cade în cache-ul de 4 s — fără asta, butoanele de poze rămâneau nelegate. */
function populateAdminUiFromSettings() {
  if (!currentSettings) return;
  const adminPage = document.getElementById('adminPage');
  if (!adminPage || !adminPage.classList.contains('visible')) return;
  adminUiPopulatedAt = Date.now();

  // ✅ populate Admin fields (company/CUI/email moved to Account page)
  // These fields are now in account page: account_op_company, account_op_cui, account_op_email

  if ($('totalTries')) {
    $('totalTries').value = currentSettings.totalTries || 0;
  }
  
  // ✅ FIX 4: Ensure updateTriesCounter is called
  if (typeof window.updateTriesCounter === 'function') {
    window.updateTriesCounter();
  }

  // 🆕 POPULATE DATE REGULAMENT
  if ($('campStart')) $('campStart').value = currentSettings.startDate || '';
  if ($('campEnd')) $('campEnd').value = currentSettings.endDate || '';

  if ($('buttonText')) $('buttonText').value = currentSettings.buttonText || 'Apasă!';
  if ($('buttonColor')) $('buttonColor').value = currentSettings.buttonColor || '#43c047';
  bindCampaignColorPreview();
  if ($('claimMessage')) $('claimMessage').value = currentSettings.claimMessage || '';
  if ($('confettiEnabled')) $('confettiEnabled').value = currentSettings.confetti || 'on';
  if ($('playsTotal')) $('playsTotal').textContent = String(currentSettings.playsTotal || 0);

  if ($('defaultPrizeMsg')) {
    $('defaultPrizeMsg').value = currentSettings.defaultPrizeMsg || 'Ne pare rău, nu ai câștigat!';
  }
  
  const defPrev = $('defaultPrizeImgPreview');
  if (defPrev) {
    // XSS-safe rendering: Use DOM API instead of innerHTML
    defPrev.replaceChildren();
    if (currentSettings.defaultPrizeImg) {
      const safeUrl = sanitizeImageSrc(currentSettings.defaultPrizeImg);
      if (safeUrl) {
        const img = document.createElement('img');
        img.src = safeUrl;
        img.style.width = '120px';
        img.style.borderRadius = '10px';
        img.style.border = '1px solid #eee';
        defPrev.appendChild(img);
      }
    }
  }

  // ✅ Background preview with local unsaved state (add/remove before Save)
  resetLocalBgImagesFromSettings();
  renderLocalBgPreview();
  bindBgUploadInput();
  bindConfirmBgUploadInput();
  resetLocalLocationHoursImageFromSettings();
  renderLocalLocationHoursPreview();
  bindLocationHoursUploadInput();
  if ($('locationHoursEnabled')) {
    $('locationHoursEnabled').checked = currentSettings?.locationHoursEnabled === true;
    syncLocationHoursEnabledLabel();
    if ($('locationHoursEnabled').dataset.boundChange !== '1') {
      $('locationHoursEnabled').addEventListener('change', syncLocationHoursEnabledLabel);
      $('locationHoursEnabled').dataset.boundChange = '1';
    }
  }
  try { hydrateContactModuleAdminFromSettings(currentSettings); } catch (e) { logger.error('[populateAdminUi] hydrateContactModuleAdminFromSettings', e); }
  if ($('locationMapEnabled')) {
    $('locationMapEnabled').checked = currentSettings?.locationMapEnabled === true;
    syncLocationMapEnabledLabel();
    if ($('locationMapEnabled').dataset.boundChange !== '1') {
      $('locationMapEnabled').addEventListener('change', syncLocationMapEnabledLabel);
      $('locationMapEnabled').dataset.boundChange = '1';
    }
  }
  if ($('locationMapName')) {
    $('locationMapName').value = (currentSettings?.locationMapName || '').trim();
  }
  if ($('locationMapAddress')) {
    $('locationMapAddress').value = (currentSettings?.locationMapAddress || '').trim();
  }
  if ($('locationMapUrl')) {
    $('locationMapUrl').value = (currentSettings?.locationMapUrl || '').trim();
  }
  resetLocalLocationLogoImageFromSettings();
  renderLocalLocationLogoPreview();
  bindLocationLogoUploadInput();
  if ($('clientTaglineInput')) {
    $('clientTaglineInput').value = String(currentSettings?.clientTagline || '').trim();
  }

  const promoG = currentSettings?.clientPromoGallery;
  if ($('clientPromoGalleryEnabled')) {
    $('clientPromoGalleryEnabled').checked = promoG?.enabled === true;
  }
  if ($('clientPromoGalleryTitle')) {
    $('clientPromoGalleryTitle').value = String(promoG?.title || '').trim().slice(0, 80);
  }
  try {
    resetLocalClientPromoGalleryFromSettings();
    renderClientPromoGalleryAdminList();
    bindClientPromoGalleryControls();
    syncClientPromoFieldsVisibility();
  } catch (e) { logger.error('[populateAdminUi] promo gallery', e); }

  try { hydrateEventModuleAdminFromSettings(currentSettings); } catch (e) { logger.error('[populateAdminUi] hydrateEventModuleAdminFromSettings', e); }

  // Single preview calls (clears then appends 1)
  renderPreview('confirmBgPreview', currentSettings?.confirmBgURL || null);

  // Slide duration
  const dSel = $('slideDuration');
  if (dSel) dSel.value = currentSettings?.slideDuration || 5;

  // Feedback settings
  if ($('feedbackEnabled')) {
    $('feedbackEnabled').checked = currentSettings?.feedbackEnabled === true;
    const feedbackFields = $('feedbackFields');
    const feedbackQrHint = $('feedbackQrHint');
    if (feedbackFields) {
      feedbackFields.style.display = currentSettings?.feedbackEnabled ? 'block' : 'none';
    }
    
    const hasFb = !!(currentSettings?.clientFeedbackUrl || '').trim();
    if (feedbackQrHint && currentSettings?.feedbackEnabled && !hasFb) {
      feedbackQrHint.style.display = 'block';
    } else if (feedbackQrHint) {
      feedbackQrHint.style.display = 'none';
    }
  }
  
  if ($('feedbackButtonLabel')) {
    $('feedbackButtonLabel').value = (currentSettings?.feedbackButtonLabel || '').trim();
  }
  if ($('feedbackCardTitle')) {
    $('feedbackCardTitle').value = currentSettings?.feedbackCardTitle || 'Recenzie';
  }
  if ($('feedbackTitle')) {
    $('feedbackTitle').value = currentSettings?.feedbackTitle || 'Îți mulțumim pentru vizită!';
  }
  
  if ($('feedbackSubtext')) {
    $('feedbackSubtext').value = currentSettings?.feedbackSubtext || 'Scanează codul pentru a ne lăsa o părere';
  }
  if ($('socialCardHint')) {
    $('socialCardHint').value = currentSettings?.socialCardHint || 'Urmărește-ne pe rețelele sociale';
  }

  if ($('clientSocialUrl')) {
    $('clientSocialUrl').value = (currentSettings?.clientSocialUrl || '').trim();
  }
  const sm = currentSettings?.socialMedia || {};
  for (const platform of ['facebook', 'instagram', 'tiktok']) {
    const p = sm[platform] || {};
    const cap = platform.charAt(0).toUpperCase() + platform.slice(1);
    const enabledEl = $(`sm${cap}Enabled`);
    const linkEl = $(`sm${cap}Link`);
    if (enabledEl) enabledEl.checked = p.enabled === true;
    if (linkEl) linkEl.value = (p.link || '').trim();
  }
  if ($('clientFeedbackUrl')) {
    $('clientFeedbackUrl').value = (currentSettings?.clientFeedbackUrl || '').trim();
  }

  // Meniu locație
  if (typeof hydrateMenuModuleAdminFromSettings === 'function') {
    try { hydrateMenuModuleAdminFromSettings(currentSettings); } catch (e) { logger.error('[populateAdminUi] hydrateMenuModuleAdminFromSettings', e); }
  }
  if ($('communityEnabled')) {
    $('communityEnabled').checked = currentSettings?.communityEnabled === true;
  }
  if ($('communityMessage')) {
    $('communityMessage').value = String(currentSettings?.communityMessage || '').trim().slice(0, 220);
  }
  if ($('wifiEnabled')) {
    $('wifiEnabled').checked = currentSettings?.wifiEnabled === true;
  }
  if ($('wifiSsid')) {
    $('wifiSsid').value = String(currentSettings?.wifiSsid || '').trim();
  }
  if ($('wifiPassword')) {
    $('wifiPassword').value = String(currentSettings?.wifiPassword || '').trim();
  }
  
  // Toggle feedback fields visibility and update hint (use onchange to prevent duplicate listeners)
  if ($('feedbackEnabled')) {
    const feedbackEnabledEl = $('feedbackEnabled');
    feedbackEnabledEl.onchange = function() {
      const feedbackFields = $('feedbackFields');
      const feedbackQrHint = $('feedbackQrHint');
      if (feedbackFields) {
        feedbackFields.style.display = this.checked ? 'block' : 'none';
      }
      
      const hasFb = !!(currentSettings?.clientFeedbackUrl || '').trim();
      if (feedbackQrHint && this.checked && !hasFb) {
        feedbackQrHint.style.display = 'block';
      } else if (feedbackQrHint) {
        feedbackQrHint.style.display = 'none';
      }
    };
  }

  // Expose preview renderer for admin UI reset paths
  if (typeof window !== 'undefined') {
    window.renderUnsavedBgPreview = renderLocalBgPreview;
  }

    // ✅ render recompense
  renderPrizeOptions();
  updateClientButtonsGuard();
  updateTriesCounter();
  
  // Update summaries and role cards after settings load
  setTimeout(() => {
    refreshAdminSummaries();
    refreshRoleCards();
    // Update admin page brand if on admin page
    import('../ui/account.js?v=83.21').then(module => {
      module.updateAdminPageBrand();
    }).catch(() => {});
  }, 150);
}

// Save admin settings
export async function saveAdminSettings(currentUser) {
  if (!currentUser) {
    throw new Error('Nu ești logat');
  }

  const uid = currentUser.uid;
  const pass = ($('adminPass').value || '').trim();
  const currentPassHash = String(currentSettings?.adminPassHash || '').trim();
  
  // If password field is filled, update it (change password)
  // If password field is empty but hash exists, keep existing password
  let passHash;
  if (pass) {
    // User wants to change/set password
    if (pass.length < 4) {
      throw new Error('Parola trebuie să aibă minim 4 caractere.');
    }
    passHash = await sha256(pass);
  } else if (currentPassHash) {
    // Keep existing password if field is empty but hash exists
    passHash = currentPassHash;
  } else {
    // PIN-ul nu mai e obligatoriu (Modul client = pagina publică)
    passHash = '';
  }

  // Validate totalTries
  const totalTriesValidation = validateNonNegativeInteger($('totalTries').value, 1000000, 'Numărul de încercări');
  if (!totalTriesValidation.valid) {
    throw new Error(totalTriesValidation.message);
  }
  const newTotal = totalTriesValidation.value;
  
  const oldTotal = parseInt(currentSettings?.totalTries || 0);
  const oldRemaining = parseInt(currentSettings?.remainingTries || 0);
  const currentCampaignStatus = currentSettings?.campaignStatus || 'draft';

  // ✅ FIX 2: Deterministic reset rule for remainingTries
  let remainingTries;
  if (newTotal !== oldTotal) {
    // ✅ FIX 2: If totalTries changed AND campaign is not active, reset remainingTries = totalTries
    if (currentCampaignStatus !== 'active') {
      remainingTries = newTotal;
    } else {
      // ✅ FIX 2: If campaign is active, adjust remainingTries based on change
      if (newTotal > oldTotal) {
        // Increased: add more tries
        remainingTries = newTotal;
      } else {
        // Decreased: keep min(remainingTries, totalTries)
        remainingTries = Math.min(oldRemaining, newTotal);
      }
    }
  } else {
    // ✅ FIX 2: If totalTries didn't change, preserve existing remainingTries
    remainingTries = Math.min(oldRemaining, newTotal);
  }
  
  // ✅ FIX: Actualizează setările locale IMEDIAT (înainte de Cloud Function)
  // Astfel, dacă totalTries se schimbă, remainingTries este resetat imediat local
  if (!currentSettings) {
    currentSettings = {};
  }
  currentSettings.totalTries = newTotal;
  currentSettings.remainingTries = remainingTries;
  // ✅ FIX: Resetează offlinePlaysPending când se schimbă totalTries
  currentSettings.offlinePlaysPending = 0;
  setCurrentSettings(currentSettings); // Actualizează memoria locală imediat

  // Validate dates if provided
  const startDate = $('campStart').value || '';
  const endDate = $('campEnd').value || '';
  if (startDate || endDate) {
    if (startDate && endDate) {
      const dateValidation = validateDateRange(startDate, endDate);
      if (!dateValidation.valid) {
        throw new Error(dateValidation.message);
      }
    } else if (startDate) {
      const startValidation = validateDate(startDate, 'Data de început');
      if (!startValidation.valid) {
        throw new Error('Data de început invalidă.');
      }
    } else if (endDate) {
      const endValidation = validateDate(endDate, 'Data de sfârșit');
      if (!endValidation.valid) {
        throw new Error('Data de sfârșit invalidă.');
      }
    }
  }

  // Validate buttonColor
  const buttonColorValidation = validateHexColor($('buttonColor').value || '#43c047');
  if (!buttonColorValidation.valid) {
    throw new Error(buttonColorValidation.message);
  }

  // Validate slideDuration
  const slideDurationValidation = validateIntegerRange($('slideDuration').value || 5, 1, 60, 'Durata slide-ului');
  if (!slideDurationValidation.valid) {
    throw new Error(slideDurationValidation.message);
  }

  const toSave = {
    // companyName, cui, operatorEmail are now saved via Account page (saveAccountSettings)
    // Keep existing values if not provided
    companyName: currentSettings?.companyName || '',
    cui: currentSettings?.cui || '',
    operatorEmail: currentSettings?.operatorEmail || '',
    totalTries: newTotal,
    remainingTries,
    // ✅ FIX 2: Add resetRemainingTries flag if campaign is not active and totalTries changed
    resetRemainingTries: (newTotal !== oldTotal && currentCampaignStatus !== 'active') || false,
    startDate,
    endDate,
    buttonText: ($('buttonText').value || 'Apasă!').trim().slice(0, 50), // Limit length
    buttonColor: buttonColorValidation.value,
    slideDuration: slideDurationValidation.value,
    adminPassHash: passHash,
    claimMessage: ($('claimMessage').value || '').trim().slice(0, 500), // Limit length
    confetti: $('confettiEnabled').value || 'on'
  };

  // Remove adminPass field if it exists (migration cleanup)
  const FieldValue = typeof firebase !== 'undefined' ? firebase.firestore.FieldValue : null;
  if (FieldValue && currentSettings?.adminPass) {
    toSave.adminPass = FieldValue.delete();
  }

  // LOGICA NOUA PENTRU BACKGROUND MULTIPLU (cu starea locală nesalvată)
  if (!Array.isArray(localBgImages) || localBgImages.length === 0) {
    resetLocalBgImagesFromSettings();
  }
  if (localBgImages.length > 5) {
    throw new Error('Poți salva maxim 5 imagini în slideshow.');
  }
  let newBgURLs = [];
  for (const item of localBgImages) {
    if (!item) continue;
    if (item.type === 'local' && item.file) {
      const fileValidation = validateCompressibleImageUpload(item.file, {
        fieldName: 'Imaginile de fundal'
      });
      if (!fileValidation.valid) {
        throw new Error(fileValidation.message);
      }
      const uploadedUrl = await uploadFile(uid, 'backgrounds', item.file);
      if (uploadedUrl) newBgURLs.push(uploadedUrl);
    } else if (item.type === 'remote' && item.url) {
      newBgURLs.push(item.url);
    }
  }
  toSave.bgURLs = newBgURLs;
  toSave.bgURL = newBgURLs.length > 0 ? newBgURLs[0] : null;

  toSave.menuQrURL = null;
  toSave.feedbackQrURL = null;

  // Feedback settings
  toSave.feedbackEnabled = $('feedbackEnabled')?.checked === true;
  toSave.feedbackButtonLabel = ($('feedbackButtonLabel')?.value || '').trim().slice(0, 40);
  toSave.feedbackCardTitle = ($('feedbackCardTitle')?.value || 'Recenzie').trim().slice(0, 40);
  toSave.feedbackTitle = ($('feedbackTitle')?.value || 'Îți mulțumim pentru vizită!').trim().slice(0, 100);
  toSave.feedbackSubtext = ($('feedbackSubtext')?.value || 'Scanează codul pentru a ne lăsa o părere').trim().slice(0, 200);
  toSave.socialCardHint = ($('socialCardHint')?.value || 'Urmărește-ne pe rețelele sociale').trim().slice(0, 80);

  const clientSocialValidation = validateMenuUrl($('clientSocialUrl')?.value);
  if (!clientSocialValidation.valid) {
    throw new Error(clientSocialValidation.message);
  }
  toSave.clientSocialUrl = clientSocialValidation.value;

  const socialMediaToSave = {};
  for (const platform of ['facebook', 'instagram', 'tiktok']) {
    const cap = platform.charAt(0).toUpperCase() + platform.slice(1);
    const enabled = $(`sm${cap}Enabled`)?.checked === true;
    const rawLink = ($(`sm${cap}Link`)?.value || '').trim();
    const linkVal = validateMenuUrl(rawLink);
    if (!linkVal.valid) {
      throw new Error(`Link ${cap} invalid: ${linkVal.message}`);
    }
    socialMediaToSave[platform] = { enabled, link: linkVal.value, qrUrl: '' };
  }
  toSave.socialMedia = socialMediaToSave;
  toSave.qrURL = null;

  const clientFeedbackValidation = validateMenuUrl($('clientFeedbackUrl')?.value);
  if (!clientFeedbackValidation.valid) {
    throw new Error(clientFeedbackValidation.message);
  }
  toSave.clientFeedbackUrl = clientFeedbackValidation.value;

  const { buildMenuModulePayload } = await import('../ui/menu-module-admin.js?v=83.21');
  const menuPayload = await buildMenuModulePayload(uid);
  Object.assign(toSave, menuPayload);
  toSave.communityEnabled = $('communityEnabled')?.checked === true;
  toSave.communityMessage = ($('communityMessage')?.value || '').trim().slice(0, 220);
  toSave.wifiEnabled = $('wifiEnabled')?.checked === true;
  toSave.locationHoursEnabled = $('locationHoursEnabled')?.checked === true;
  if (localLocationHoursImage?.type === 'local' && localLocationHoursImage.file) {
    const locHoursValidation = validateFileUpload(localLocationHoursImage.file, {
      maxSize: 10 * 1024 * 1024,
      allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      fieldName: 'Imaginea program'
    });
    if (!locHoursValidation.valid) {
      throw new Error(locHoursValidation.message);
    }
    toSave.locationHoursImageURL = await uploadFile(uid, 'location-hours', localLocationHoursImage.file);
  } else if (localLocationHoursImage?.type === 'remote' && localLocationHoursImage.url) {
    toSave.locationHoursImageURL = localLocationHoursImage.url;
  } else if (hasUnsavedLocationHoursChanges) {
    toSave.locationHoursImageURL = null;
  } else {
    toSave.locationHoursImageURL = currentSettings?.locationHoursImageURL || null;
  }
  if (toSave.locationHoursEnabled && !toSave.locationHoursImageURL) {
    throw new Error('Încarcă o imagine pentru program înainte de activare.');
  }

  const { buildContactModulePayload } = await import('../ui/contact-module-admin.js?v=83.21');
  Object.assign(toSave, buildContactModulePayload());

  const locationMapFields = readLocationMapFieldsFromForm();
  toSave.locationMapEnabled = locationMapFields.enabled;
  toSave.locationMapName = locationMapFields.name;
  toSave.locationMapAddress = locationMapFields.address;
  if (locationMapFields.enabled) {
    if (!locationMapFields.urlValidation.valid) {
      throw new Error(locationMapFields.urlValidation.message);
    }
    if (!locationMapFields.urlValidation.value) {
      throw new Error('Introdu linkul Google Maps.');
    }
    toSave.locationMapUrl = locationMapFields.urlValidation.value;
  } else if ($('locationMapUrl')?.value?.trim()) {
    if (!locationMapFields.urlValidation.valid) {
      throw new Error(locationMapFields.urlValidation.message);
    }
    toSave.locationMapUrl = locationMapFields.urlValidation.value;
  } else {
    toSave.locationMapUrl = '';
  }

  if (localLocationLogoImage?.type === 'local' && localLocationLogoImage.file) {
    const logoValidation = validateFileUpload(localLocationLogoImage.file, {
      maxSize: 10 * 1024 * 1024,
      allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      fieldName: 'Logo locație'
    });
    if (!logoValidation.valid) {
      throw new Error(logoValidation.message);
    }
    toSave.locationLogoURL = await uploadFile(uid, 'location-logo', localLocationLogoImage.file);
  } else if (localLocationLogoImage?.type === 'remote' && localLocationLogoImage.url) {
    toSave.locationLogoURL = localLocationLogoImage.url;
  } else if (hasUnsavedLocationLogoChanges) {
    toSave.locationLogoURL = null;
  } else {
    toSave.locationLogoURL = currentSettings?.locationLogoURL || null;
  }

  toSave.wifiSsid = ($('wifiSsid')?.value || '').trim().slice(0, 64);
  toSave.wifiPassword = ($('wifiPassword')?.value || '').trim().slice(0, 128);

  // Validate and upload confirmation background
  const confF = $('confirmBgUpload').files[0];
  if (confF) {
    const confValidation = validateCompressibleImageUpload(confF, {
      fieldName: 'Imaginea de fundal pentru confirmare'
    });
    if (!confValidation.valid) {
      throw new Error(confValidation.message);
    }
    toSave.confirmBgURL = await uploadFile(uid, 'confirm_bg', confF);
  } else {
    toSave.confirmBgURL = currentSettings?.confirmBgURL || null;
  }

  // Validate and upload default prize image
  const defF = $('defaultPrizeImg').files[0];
  if (defF) {
    const defValidation = validateFileUpload(defF, {
      maxSize: 10 * 1024 * 1024,
      allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      fieldName: 'Imaginea recompensei implicite'
    });
    if (!defValidation.valid) {
      throw new Error(defValidation.message);
    }
    toSave.defaultPrizeImg = await uploadFile(uid, 'prizes_default', defF);
  } else {
    toSave.defaultPrizeImg = currentSettings?.defaultPrizeImg || null;
  }

  toSave.defaultPrizeMsg =
    $('defaultPrizeMsg').value ||
    currentSettings?.defaultPrizeMsg ||
    'Ne pare rău, nu ai câștigat!';

  // ✅ prizes
  const prizes = [];
  const cards = [...document.querySelectorAll('#prizeContainer .prize-card')];

  for (const card of cards) {
    const msg = (card.querySelector('.prizeMsg')?.value || '').trim().slice(0, 200); // Limit message length
    const countInput = card.querySelector('.prizeCount')?.value;
    const fileProbe = card.querySelector('.prizeImg')?.files?.[0] || null;
    const existingProbe = card.querySelector('img')?.getAttribute('data-src') || null;
    // Card gol (fără nume, fără bucăți, fără poză) — ignorat, ca la salvarea parțială
    if (!msg && !(Number(countInput) > 0) && !fileProbe && !existingProbe) continue;
    if (!msg) {
      throw new Error('Scrie numele fiecărei recompense (ex. „O cafea gratuită”).');
    }
    
    // Validate prize count
    const countValidation = validateNonNegativeInteger(countInput, 10000, 'Numărul de recompense');
    if (!countValidation.valid) {
      throw new Error(`Recompensă "${msg || 'fără nume'}": ${countValidation.message}`);
    }
    const count = countValidation.value;

    const file = card.querySelector('.prizeImg')?.files?.[0] || null;
    const existing = card.querySelector('img')?.getAttribute('data-src') || null;

    // Validate prize image if provided
    let image = existing;
    if (file) {
      const imageValidation = validateFileUpload(file, {
        maxSize: 10 * 1024 * 1024,
        allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
        fieldName: `Imaginea recompensei "${msg || 'fără nume'}"`
      });
      if (!imageValidation.valid) {
        throw new Error(imageValidation.message);
      }
      image = await uploadFile(uid, 'prizes', file);
    }
    
    // ✅ FIX: Generate unique ID for prize if it doesn't have one
    // Check if this prize already exists in current settings (by message+image match)
    // to preserve existing ID, otherwise generate new one
    const currentPrizes = currentSettings?.prizes || [];
    let prizeId = null;
    
    // Try to find existing prize by message+image to preserve ID
    const existingPrize = currentPrizes.find(p => 
      String(p.message || '').trim() === String(msg).trim() &&
      String(p.image || '') === String(image || '')
    );
    
    if (existingPrize && (existingPrize.id || existingPrize.uid || existingPrize.key)) {
      // Preserve existing ID
      prizeId = existingPrize.id || existingPrize.uid || existingPrize.key;
    } else {
      // Generate new unique ID
      prizeId = `prize_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
    
    prizes.push({ 
      id: prizeId, // ✅ Add unique ID
      message: msg, 
      count, 
      image: image || null 
    });
  }
  toSave.prizes = prizes;

  const promoEnabled = $('clientPromoGalleryEnabled')?.checked === true;
  const promoTitle = ($('clientPromoGalleryTitle')?.value || '').trim().slice(0, 80);
  if (!Array.isArray(localClientPromoGalleryImages) || localClientPromoGalleryImages.length === 0) {
    resetLocalClientPromoGalleryFromSettings();
  }
  if (localClientPromoGalleryImages.length > 10) {
    throw new Error('Poți salva maxim 10 imagini în galeria promo.');
  }
  const promoImages = [];
  const promoLabels = [];
  const promoLinks = [];
  for (const item of localClientPromoGalleryImages) {
    if (!item) continue;
    if (item.type === 'local' && item.file) {
      const promoFileValidation = validateFileUpload(item.file, {
        maxSize: 10 * 1024 * 1024,
        allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
        fieldName: 'Imagine galerie promo'
      });
      if (!promoFileValidation.valid) {
        throw new Error(promoFileValidation.message);
      }
      const uploadedPromoUrl = await uploadFile(uid, 'client-promo', item.file);
      if (uploadedPromoUrl) { promoImages.push(uploadedPromoUrl); promoLabels.push(String(item.label || '').trim().slice(0, 40)); promoLinks.push(promoLinkOf(item)); }
    } else if (item.type === 'remote' && item.url) {
      promoImages.push(item.url);
      promoLabels.push(String(item.label || '').trim().slice(0, 40));
      promoLinks.push(promoLinkOf(item));
    }
  }
  if (promoEnabled && promoImages.length < 1) {
    throw new Error('Galerie promo: încarcă cel puțin o imagine sau dezactivează blocul.');
  }
  toSave.clientPromoGallery = {
    enabled: promoEnabled,
    title: promoTitle,
    images: promoImages,
    labels: promoLabels,
    links: promoLinks
  };
  toSave.clientLayoutConfig = sanitizeClientLayoutConfig(currentSettings?.clientLayoutConfig);
  toSave.clientTemplateStyle = String(currentSettings?.clientTemplateStyle || 'cloud').trim().toLowerCase();
  toSave.clientTemplateAiVariant = currentSettings?.clientTemplateAiVariant || null;

  // Use Cloud Function instead of direct Firestore write
  // Note: File uploads are done client-side (to Storage), then URLs are sent to Cloud Function
  await saveCampaignSettingsBackend(toSave);

  // ✅ FIX: Reasigură-te că setările locale sunt actualizate (după Cloud Function)
  currentSettings.totalTries = newTotal;
  currentSettings.remainingTries = remainingTries;
  // ✅ FIX: Resetează offlinePlaysPending când se schimbă totalTries
  currentSettings.offlinePlaysPending = 0;
  currentSettings.menuUrl = toSave.menuUrl;
  currentSettings.menuLabel = toSave.menuLabel;
  currentSettings.menuImages = toSave.menuImages;
  currentSettings.menuEnabled = toSave.menuEnabled;
  currentSettings.communityEnabled = toSave.communityEnabled;
  currentSettings.communityMessage = toSave.communityMessage;
  currentSettings.menuQrURL = toSave.menuQrURL;
  currentSettings.qrURL = toSave.qrURL;
  currentSettings.feedbackQrURL = toSave.feedbackQrURL;
  currentSettings.bgURLs = toSave.bgURLs;
  currentSettings.bgURL = toSave.bgURL;
  currentSettings.clientSocialUrl = toSave.clientSocialUrl;
  currentSettings.socialMedia = toSave.socialMedia;
  currentSettings.clientFeedbackUrl = toSave.clientFeedbackUrl;
  currentSettings.clientPromoGallery = toSave.clientPromoGallery;
  currentSettings.clientLayoutConfig = toSave.clientLayoutConfig;
  currentSettings.clientTemplateStyle = toSave.clientTemplateStyle;
  currentSettings.clientTemplateAiVariant = toSave.clientTemplateAiVariant;
  currentSettings.locationHoursEnabled = toSave.locationHoursEnabled;
  currentSettings.locationHoursImageURL = toSave.locationHoursImageURL;
  currentSettings.reservationLabel = toSave.reservationLabel;
  currentSettings.reservationEnabled = toSave.reservationEnabled;
  currentSettings.reservationPhone = toSave.reservationPhone;
  currentSettings.reservationMessage = toSave.reservationMessage;
  currentSettings.locationMapEnabled = toSave.locationMapEnabled;
  currentSettings.locationMapName = toSave.locationMapName;
  currentSettings.locationMapAddress = toSave.locationMapAddress;
  currentSettings.locationMapUrl = toSave.locationMapUrl;
  currentSettings.locationLogoURL = toSave.locationLogoURL;
  setCurrentSettings(currentSettings);

  // Reset local unsaved state after successful persistence
  localBgImages.forEach((item) => {
    if (item?.type === 'local' && item.previewUrl) {
      try {
        URL.revokeObjectURL(item.previewUrl);
      } catch (e) {
        // Non-critical
      }
    }
  });
  resetLocalBgImagesFromSettings();
  renderLocalBgPreview();
  hydrateMenuModuleAdminFromSettings(currentSettings);
  resetLocalClientPromoGalleryFromSettings();
  renderClientPromoGalleryAdminList();
  resetLocalLocationHoursImageFromSettings();
  renderLocalLocationHoursPreview();
  resetLocalLocationLogoImageFromSettings();
  renderLocalLocationLogoPreview();
  refreshAdminSummaries();
  showSnackbar('Imaginile au fost salvate', 'success');

  // Actualizează UI-ul imediat (nu așteaptă reload)
  updateTriesCounter();
  updateClientButtonsGuard();

  // ✅ FIX: Reload în background cu delay pentru a permite Cloud Function-ului să termine salvare
  // Delay de 3 secunde pentru a evita suprascrierea setărilor locale cu cele vechi din Firestore
  setTimeout(() => {
    loadSettings(uid, true).catch(err => {
      logger.warn('[saveAdminSettings] Background reload failed (non-critical):', err);
    });
  }, 3000);

  // Reapply local background if on admin/account page (NU mai aplicăm Firebase)
  const currentPage = document.querySelector('.page.visible')?.id;
  if (currentPage === 'adminPage' || currentPage === 'accountPage') {
    import('../utils/helpers.js?v=83.21').then(m => m.applyLocalBackground()).catch(() => {});
  }

  // curățăm input-urile de fișiere
  [
    'bgUpload',
    'feedbackQrUpload',
    'qrUpload',
    'clientPromoGalleryUpload',
    'confirmBgUpload',
    'defaultPrizeImg',
    'locationHoursUpload',
    'locationLogoUpload'
  ].forEach(id => {
    const el = $(id);
    if (el) el.value = '';
  });
}

async function afterClientModuleSave(uid, patch, hooks = {}) {
  await saveCampaignSettingsBackend(patch);
  if (currentSettings) {
    Object.assign(currentSettings, patch);
    setCurrentSettings(currentSettings);
  }
  if (typeof hooks.afterLocal === 'function') {
    await hooks.afterLocal();
  }
  refreshAdminSummaries();
  if (hooks.applyClientUI) {
    try {
      const { applyClientUI } = await import('../ui/slideshow.js?v=83.21');
      applyClientUI();
    } catch (e) {
      logger.warn('[client-interface-module-save] applyClientUI failed (non-critical):', e);
    }
  }
  if (uid) {
    setTimeout(() => {
      loadSettings(uid, true).catch((err) => {
        logger.warn('[client-interface-module-save] Background reload failed:', err);
      });
    }, 3000);
  }
}

/** Salvare parțială per modul (fără salvare globală). */
export async function saveClientInterfaceModulePartial(moduleId) {
  const currentUser = getCurrentUser();
  if (!currentUser) {
    throw new Error('Nu ești logat');
  }
  const uid = currentUser.uid;
  const id = String(moduleId || '').trim();

  if (id === 'images') {
    if (!Array.isArray(localBgImages) || localBgImages.length === 0) {
      resetLocalBgImagesFromSettings();
    }
    if (localBgImages.length > 5) {
      throw new Error('Poți salva maxim 5 imagini în slideshow.');
    }
    const slideDurationValidation = validateIntegerRange($('slideDuration').value || 5, 1, 60, 'Durata slide-ului');
    if (!slideDurationValidation.valid) {
      throw new Error(slideDurationValidation.message);
    }
    const newBgURLs = [];
    for (const item of localBgImages) {
      if (!item) continue;
      if (item.type === 'local' && item.file) {
        const fileValidation = validateCompressibleImageUpload(item.file, {
          fieldName: 'Imaginile de fundal'
        });
        if (!fileValidation.valid) throw new Error(fileValidation.message);
        const uploadedUrl = await uploadFile(uid, 'backgrounds', item.file);
        if (uploadedUrl) newBgURLs.push(uploadedUrl);
      } else if (item.type === 'remote' && item.url) {
        newBgURLs.push(item.url);
      }
    }
    const toSave = {
      bgURLs: newBgURLs,
      bgURL: newBgURLs.length > 0 ? newBgURLs[0] : null,
      slideDuration: slideDurationValidation.value
    };
    const confF = $('confirmBgUpload')?.files?.[0];
    if (confF) {
      const confValidation = validateCompressibleImageUpload(confF, {
        fieldName: 'Imaginea de fundal pentru confirmare'
      });
      if (!confValidation.valid) throw new Error(confValidation.message);
      toSave.confirmBgURL = await uploadFile(uid, 'confirm_bg', confF);
    } else {
      toSave.confirmBgURL = currentSettings?.confirmBgURL || null;
    }
    await afterClientModuleSave(uid, toSave, {
      afterLocal: async () => {
        resetLocalBgImagesFromSettings();
        renderLocalBgPreview();
        const el = $('bgUpload');
        if (el) el.value = '';
        const confEl = $('confirmBgUpload');
        if (confEl) confEl.value = '';
      },
      applyClientUI: true
    });
    return;
  }

  if (id === 'social-media') {
    const socialMediaToSave = {};
    for (const platform of ['facebook', 'instagram', 'tiktok']) {
      const cap = platform.charAt(0).toUpperCase() + platform.slice(1);
      const enabled = $(`sm${cap}Enabled`)?.checked === true;
      const rawLink = ($(`sm${cap}Link`)?.value || '').trim();
      const linkVal = validateMenuUrl(rawLink);
      if (!linkVal.valid) throw new Error(`Link ${cap} invalid: ${linkVal.message}`);
      socialMediaToSave[platform] = { enabled, link: linkVal.value, qrUrl: '' };
    }
    await afterClientModuleSave(uid, {
      socialMedia: socialMediaToSave,
      qrURL: null,
      socialCardHint: ($('socialCardHint')?.value || 'Urmărește-ne pe rețelele sociale').trim().slice(0, 80)
    }, {
      applyClientUI: true
    });
    return;
  }

  if (id === 'feedback') {
    const toSave = {
      feedbackEnabled: $('feedbackEnabled')?.checked === true,
      feedbackButtonLabel: ($('feedbackButtonLabel')?.value || '').trim().slice(0, 40),
      feedbackCardTitle: ($('feedbackCardTitle')?.value || 'Recenzie').trim().slice(0, 40),
      feedbackTitle: ($('feedbackTitle')?.value || 'Îți mulțumim pentru vizită!').trim().slice(0, 100),
      feedbackSubtext: ($('feedbackSubtext')?.value || '').trim().slice(0, 200)
    };
    const clientFeedbackValidation = validateMenuUrl($('clientFeedbackUrl')?.value);
    if (!clientFeedbackValidation.valid) throw new Error(clientFeedbackValidation.message);
    toSave.clientFeedbackUrl = clientFeedbackValidation.value;
    toSave.feedbackQrURL = null;
    await afterClientModuleSave(uid, toSave, {
      applyClientUI: true
    });
    return;
  }

  if (id === 'location-menu') {
    const { saveMenuModuleSettings } = await import('../ui/menu-module-admin.js?v=83.21');
    await saveMenuModuleSettings({ quiet: true });
    return;
  }

  if (id === 'wifi-info') {
    await afterClientModuleSave(
      uid,
      {
        wifiEnabled: $('wifiEnabled')?.checked === true,
        wifiSsid: ($('wifiSsid')?.value || '').trim().slice(0, 64),
        wifiPassword: ($('wifiPassword')?.value || '').trim().slice(0, 128)
      },
      { applyClientUI: true }
    );
    return;
  }

  if (id === 'community-page') {
    await afterClientModuleSave(
      uid,
      {
        communityEnabled: $('communityEnabled')?.checked === true,
        communityMessage: ($('communityMessage')?.value || '').trim().slice(0, 220)
      },
      { applyClientUI: true }
    );
    return;
  }

  if (id === 'client-promo-gallery') {
    const promoEnabled = $('clientPromoGalleryEnabled')?.checked === true;
    const promoTitle = ($('clientPromoGalleryTitle')?.value || '').trim().slice(0, 80);
    if (!Array.isArray(localClientPromoGalleryImages) || localClientPromoGalleryImages.length === 0) {
      resetLocalClientPromoGalleryFromSettings();
    }
    if (localClientPromoGalleryImages.length > 10) {
      throw new Error('Poți salva maxim 10 imagini în galeria promo.');
    }
    const promoImages = [];
    const promoLabels = [];
    const promoLinks = [];
    for (const item of localClientPromoGalleryImages) {
      if (!item) continue;
      if (item.type === 'local' && item.file) {
        const v = validateFileUpload(item.file, {
          maxSize: 10 * 1024 * 1024,
          allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
          fieldName: 'Imagine galerie promo'
        });
        if (!v.valid) throw new Error(v.message);
        const url = await uploadFile(uid, 'client-promo', item.file);
        if (url) { promoImages.push(url); promoLabels.push(String(item.label || '').trim().slice(0, 40)); promoLinks.push(promoLinkOf(item)); }
      } else if (item.type === 'remote' && item.url) {
        promoImages.push(item.url);
        promoLabels.push(String(item.label || '').trim().slice(0, 40));
        promoLinks.push(promoLinkOf(item));
      }
    }
    if (promoEnabled && promoImages.length < 1) {
      throw new Error('Galerie promo: încarcă cel puțin o imagine sau dezactivează blocul.');
    }
    await afterClientModuleSave(
      uid,
      {
        clientPromoGallery: { enabled: promoEnabled, title: promoTitle, images: promoImages, labels: promoLabels, links: promoLinks }
      },
      {
        afterLocal: async () => {
          resetLocalClientPromoGalleryFromSettings();
          renderClientPromoGalleryAdminList();
          const el = $('clientPromoGalleryUpload');
          if (el) el.value = '';
        },
        applyClientUI: true
      }
    );
    return;
  }

  if (id === 'admin') {
    const pass = ($('adminPass')?.value || '').trim();
    const currentPassHash = String(currentSettings?.adminPassHash || '').trim();
    let passHash;
    if (pass) {
      if (pass.length < 4) throw new Error('Parola trebuie să aibă minim 4 caractere.');
      passHash = await sha256(pass);
    } else if (currentPassHash) {
      passHash = currentPassHash;
    } else {
      passHash = '';
    }
    const totalTriesValidation = validateNonNegativeInteger($('totalTries').value, 1000000, 'Numărul de încercări');
    if (!totalTriesValidation.valid) throw new Error(totalTriesValidation.message);
    const newTotal = totalTriesValidation.value;
    const oldTotal = parseInt(currentSettings?.totalTries || 0);
    const oldRemaining = parseInt(currentSettings?.remainingTries || 0);
    const currentCampaignStatus = currentSettings?.campaignStatus || 'draft';
    let remainingTries;
    if (newTotal !== oldTotal) {
      if (currentCampaignStatus !== 'active') {
        remainingTries = newTotal;
      } else {
        remainingTries = newTotal > oldTotal ? newTotal : Math.min(oldRemaining, newTotal);
      }
    } else {
      remainingTries = Math.min(oldRemaining, newTotal);
    }
    const startDate = $('campStart').value || '';
    const endDate = $('campEnd').value || '';
    if (startDate || endDate) {
      if (startDate && endDate) {
        const dateValidation = validateDateRange(startDate, endDate);
        if (!dateValidation.valid) throw new Error(dateValidation.message);
      }
    }
    const buttonColorValidation = validateHexColor($('buttonColor').value || '#43c047');
    if (!buttonColorValidation.valid) throw new Error(buttonColorValidation.message);
    const toSave = {
      totalTries: newTotal,
      remainingTries,
      resetRemainingTries: (newTotal !== oldTotal && currentCampaignStatus !== 'active') || false,
      startDate,
      endDate,
      buttonText: ($('buttonText').value || 'Apasă!').trim().slice(0, 50),
      buttonColor: buttonColorValidation.value,
      adminPassHash: passHash,
      claimMessage: ($('claimMessage').value || '').trim().slice(0, 500),
      confetti: $('confettiEnabled').value || 'on'
    };
    await afterClientModuleSave(uid, toSave, {
      afterLocal: async () => {
        updateTriesCounter();
        updateClientButtonsGuard();
      }
    });
    return;
  }

  if (id === 'prizes') {
    const prizes = [];
    const cards = [...document.querySelectorAll('#prizeContainer .prize-card')];
    for (const card of cards) {
      const msg = (card.querySelector('.prizeMsg')?.value || '').trim().slice(0, 200);
      const countInput = card.querySelector('.prizeCount')?.value;
      const file = card.querySelector('.prizeImg')?.files?.[0] || null;
      const existing = card.querySelector('img')?.getAttribute('data-src') || null;
      // Card complet gol (fără nume, fără bucăți, fără poză) — îl ignorăm în loc să salvăm un premiu fantomă
      if (!msg && !(Number(countInput) > 0) && !file && !existing) continue;
      if (!msg) {
        throw new Error('Scrie numele fiecărei recompense (ex. „O cafea gratuită”).');
      }
      const countValidation = validateNonNegativeInteger(countInput, 10000, 'Numărul de recompense');
      if (!countValidation.valid) {
        throw new Error(`Recompensă "${msg || 'fără nume'}": ${countValidation.message}`);
      }
      let image = existing;
      if (file) {
        const imageValidation = validateFileUpload(file, {
          maxSize: 10 * 1024 * 1024,
          allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
          fieldName: `Imaginea recompensei "${msg || 'fără nume'}"`
        });
        if (!imageValidation.valid) throw new Error(imageValidation.message);
        image = await uploadFile(uid, 'prizes', file);
      }
      const currentPrizes = currentSettings?.prizes || [];
      let prizeId = null;
      const existingPrize = currentPrizes.find(
        (p) =>
          String(p.message || '').trim() === String(msg).trim() &&
          String(p.image || '') === String(image || '')
      );
      if (existingPrize && (existingPrize.id || existingPrize.uid || existingPrize.key)) {
        prizeId = existingPrize.id || existingPrize.uid || existingPrize.key;
      } else {
        prizeId = `prize_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      }
      prizes.push({ id: prizeId, message: msg, count: countValidation.value, image: image || null });
    }
    await afterClientModuleSave(uid, { prizes }, { applyClientUI: true });
    return;
  }

  if (id === 'noWin') {
    const toSave = {
      defaultPrizeMsg:
        $('defaultPrizeMsg').value || currentSettings?.defaultPrizeMsg || 'Ne pare rău, nu ai câștigat!'
    };
    const defF = $('defaultPrizeImg')?.files?.[0];
    if (defF) {
      const defValidation = validateFileUpload(defF, {
        maxSize: 10 * 1024 * 1024,
        allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
        fieldName: 'Imaginea recompensei implicite'
      });
      if (!defValidation.valid) throw new Error(defValidation.message);
      toSave.defaultPrizeImg = await uploadFile(uid, 'prizes_default', defF);
    } else {
      toSave.defaultPrizeImg = currentSettings?.defaultPrizeImg || null;
    }
    await afterClientModuleSave(uid, toSave, {
      afterLocal: async () => {
        const el = $('defaultPrizeImg');
        if (el) el.value = '';
      },
      applyClientUI: true
    });
    return;
  }

  throw new Error(`Modul necunoscut: ${id}`);
}

export async function saveLocationHoursSettings(options = {}) {
  const { quiet = false } = options;
  const currentUser = getCurrentUser();
  if (!currentUser) {
    throw new Error('Nu ești logat');
  }
  const uid = currentUser.uid;
  const enabled = $('locationHoursEnabled')?.checked === true;
  const toSave = { locationHoursEnabled: enabled };

  if (localLocationHoursImage?.type === 'local' && localLocationHoursImage.file) {
    const validation = validateFileUpload(localLocationHoursImage.file, {
      maxSize: 10 * 1024 * 1024,
      allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      fieldName: 'Imaginea program'
    });
    if (!validation.valid) {
      throw new Error(validation.message);
    }
    toSave.locationHoursImageURL = await uploadFile(uid, 'location-hours', localLocationHoursImage.file);
  } else if (localLocationHoursImage?.type === 'remote' && localLocationHoursImage.url) {
    toSave.locationHoursImageURL = localLocationHoursImage.url;
  } else if (hasUnsavedLocationHoursChanges) {
    toSave.locationHoursImageURL = null;
  } else {
    toSave.locationHoursImageURL = currentSettings?.locationHoursImageURL || null;
  }

  if (enabled && !toSave.locationHoursImageURL) {
    throw new Error('Încarcă o imagine pentru program înainte de activare.');
  }

  await saveCampaignSettingsBackend(toSave);

  if (currentSettings) {
    currentSettings.locationHoursEnabled = toSave.locationHoursEnabled;
    currentSettings.locationHoursImageURL = toSave.locationHoursImageURL;
    setCurrentSettings(currentSettings);
  }

  resetLocalLocationHoursImageFromSettings();
  renderLocalLocationHoursPreview();
  syncLocationHoursEnabledLabel();
  refreshAdminSummaries();

  const { applyClientUI } = await import('../ui/slideshow.js?v=83.21');
  applyClientUI();

  if (!quiet) {
    showSnackbar('Imagine program salvată cu succes.', 'success');
  }

  setTimeout(() => {
    loadSettings(uid, true).catch((err) => {
      logger.warn('[saveLocationHoursSettings] Background reload failed:', err);
    });
  }, 3000);

  const uploadEl = $('locationHoursUpload');
  if (uploadEl) uploadEl.value = '';
}

function readClientTaglineInput() {
  const raw = String($('clientTaglineInput')?.value || '').trim();
  return raw ? raw.slice(0, 120) : null;
}

async function persistLocationBrandSettings(uid, patch = {}) {
  const toSave = { ...patch };
  await saveCampaignSettingsBackend(toSave);
  if (currentSettings) {
    if (toSave.locationLogoURL !== undefined) {
      currentSettings.locationLogoURL = toSave.locationLogoURL;
    }
    if (toSave.clientTagline !== undefined) {
      currentSettings.clientTagline = toSave.clientTagline;
    }
    setCurrentSettings(currentSettings);
  }
  if (toSave.locationLogoURL !== undefined) {
    resetLocalLocationLogoImageFromSettings();
    renderLocalLocationLogoPreview();
  }
  refreshAdminSummaries();
  setTimeout(() => {
    loadSettings(uid, true).catch((err) => {
      logger.warn('[locationLogo] Background reload failed:', err);
    });
  }, 3000);
}

async function persistLocationLogoUrl(uid, url) {
  await persistLocationBrandSettings(uid, { locationLogoURL: url || null });
}

export async function saveLocationLogoSettings(options = {}) {
  const { quiet = false } = options;
  const currentUser = getCurrentUser();
  if (!currentUser) {
    throw new Error('Nu ești logat');
  }
  const uid = currentUser.uid;
  const clientTagline = readClientTaglineInput();
  const taglineChanged =
    String(currentSettings?.clientTagline || '').trim() !== String(clientTagline || '').trim();
  const patch = { clientTagline };

  if (localLocationLogoImage?.type === 'local' && localLocationLogoImage.file) {
    const validation = validateFileUpload(localLocationLogoImage.file, {
      maxSize: 10 * 1024 * 1024,
      allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      fieldName: 'Logo locație'
    });
    if (!validation.valid) {
      throw new Error(validation.message);
    }
    const uploadedUrl = await uploadFile(uid, 'location-logo', localLocationLogoImage.file);
    patch.locationLogoURL = uploadedUrl;
    await persistLocationBrandSettings(uid, patch);
    if (!quiet) {
      showSnackbar(
        'Logo și slogan salvate. Reîncarcă linkul public (/m/…) ca să apară la încărcare.',
        'success'
      );
    }
  } else if (localLocationLogoImage?.type === 'remote' && localLocationLogoImage.url) {
    patch.locationLogoURL = localLocationLogoImage.url;
    await persistLocationBrandSettings(uid, patch);
    if (!quiet) {
      showSnackbar(
        'Logo și slogan salvate. Reîncarcă linkul public (/m/…) ca să apară la încărcare.',
        'success'
      );
    }
  } else if (taglineChanged) {
    await persistLocationBrandSettings(uid, patch);
    if (!quiet) {
      showSnackbar('Slogan salvat pentru template Elegant.', 'success');
    }
  } else {
    throw new Error('Încarcă o imagine pentru logo sau modifică sloganul.');
  }

  const uploadEl = $('locationLogoUpload');
  if (uploadEl) uploadEl.value = '';
}

export async function deleteLocationLogo() {
  const currentUser = getCurrentUser();
  if (!currentUser) {
    throw new Error('Nu ești logat');
  }
  const uid = currentUser.uid;
  if (localLocationLogoImage?.type === 'local' && localLocationLogoImage.previewUrl) {
    try {
      URL.revokeObjectURL(localLocationLogoImage.previewUrl);
    } catch (e) {
      // Non-critical
    }
  }
  localLocationLogoImage = null;
  setLocationLogoDirtyState(false);
  await persistLocationLogoUrl(uid, null);
  showSnackbar('Logo șters.', 'success');
  const uploadEl = $('locationLogoUpload');
  if (uploadEl) uploadEl.value = '';
}

export async function saveLocationMapSettings(options = {}) {
  const { quiet = false } = options;
  const currentUser = getCurrentUser();
  if (!currentUser) {
    throw new Error('Nu ești logat');
  }
  const uid = currentUser.uid;
  const fields = readLocationMapFieldsFromForm();

  if (fields.enabled) {
    if (!fields.urlValidation.valid) {
      throw new Error(fields.urlValidation.message);
    }
    if (!fields.urlValidation.value) {
      throw new Error('Introdu linkul Google Maps.');
    }
  } else if ($('locationMapUrl')?.value?.trim() && !fields.urlValidation.valid) {
    throw new Error(fields.urlValidation.message);
  }

  const toSave = {
    locationMapEnabled: fields.enabled,
    locationMapName: fields.name,
    locationMapAddress: fields.address,
    locationMapUrl: fields.urlValidation.valid ? fields.urlValidation.value : ''
  };

  await saveCampaignSettingsBackend(toSave);

  if (currentSettings) {
    currentSettings.locationMapEnabled = toSave.locationMapEnabled;
    currentSettings.locationMapName = toSave.locationMapName;
    currentSettings.locationMapAddress = toSave.locationMapAddress;
    currentSettings.locationMapUrl = toSave.locationMapUrl;
    setCurrentSettings(currentSettings);
  }

  syncLocationMapEnabledLabel();
  refreshAdminSummaries();

  const { applyClientUI } = await import('../ui/slideshow.js?v=83.21');
  applyClientUI();

  if (!quiet) {
    showSnackbar('Locația a fost salvată.', 'success');
  }

  setTimeout(() => {
    loadSettings(uid, true).catch((err) => {
      logger.warn('[saveLocationMapSettings] Background reload failed:', err);
    });
  }, 3000);
}

// Reset tries
export async function resetTries(uid) {
  if (!currentSettings) return;
  
  try {
    // Use Cloud Function instead of direct Firestore write
    const { resetTriesBackend } = await import('./backend.js?v=83.21');
    await resetTriesBackend();
    
    // Update local settings
    currentSettings.remainingTries = currentSettings.totalTries || 0;
    // ✅ FIX: Resetează offlinePlaysPending la reset tries
    currentSettings.offlinePlaysPending = 0;
    // ✅ FIX: Persistă remainingTries în localStorage
    setCurrentSettings(currentSettings);
    updateTriesCounter();
  } catch (e) {
    logger.error('[resetTries] Error:', e);
    throw e;
  }
}
